import * as __static_import_0 from "./gl/material_atlas.js";import * as __static_import_1 from "./engine_resolve/band_select.js";import { fetchAsset } from './asset_url.js';
import { NollaPrng } from './nolla_prng.js';
import { BLOCKED_COLORS, GENERAL_SCENES, PIXEL_SCENE_BIOME_MAP } from './pixel_scene_config.js';
import { MATERIAL_COLOR_CONVERSION, MATERIAL_COLOR_LOOKUP, MATERIAL_DATA, MATERIAL_WANG_COLORS } from './potion_config.js';
import { getBiomeAtWorldCoordinates } from './utils.js';
import { biomeEdgeNoiseFlag } from './wobble_flags.js';
import { loadPNG } from './png_sanitizer.js';
import { prescanPixelScene } from './poi_scanner.js';
import { BIOME_BACKGROUND_COLORS, TILE_OVERLAY_COLORS, channelDistance, makeBlackTransparent, materialDisplayColor, sceneMaterialFillColorForBiome, terrainFillColorForBiome } from './image_processing.js';
import { FILL_BIOME_MATERIALS, GENERATOR_CONFIG } from './generator_config.js';
import { appSettings } from './settings.js';
// Scenes with a per-pixel cell-color override ("visual" art): the engine's
// LoadPixelScene colors file paints each cell's color instead of the
// material's texture/flat color (the giant tree's bark, the robot eggs, the
// holy-mountain hall art). Generated by tools/gen_visual_overlays.mjs; keys
// are `${getBiomeAlias(biome)}/${sceneName}`.
import { VISUAL_OVERLAY_SCENES } from './pixel_scene_visuals.js';
// Scenes the engine draws a BACKGROUND sprite behind (LoadPixelScene's
// background_filename, z = 50): the image covers the scene's rectangle behind
// every cell, so it shows through the scene's air, through gaps its art never
// paints, and under its translucent liquids. Generated by
// tools/gen_scene_backgrounds.mjs; keys are `${getBiomeAlias(biome)}/${sceneName}`.
import { SCENE_BACKGROUNDS, SCENE_BACKGROUNDS_BY_BIOME } from './pixel_scene_backgrounds.js';
import { SKIP_EDGE_TEXTURE_SCENES } from './pixel_scene_edge_flags.js';
import { SCENE_CELL_AIR, SCENE_CELL_COLOR, SCENE_CELL_DENSITY, SCENE_CELL_MATERIAL } from './scene_cells.js';

// Scenes the game paints with ANOTHER scene's colors file. VISUAL_OVERLAY_SCENES
// is derived from the "<name>_visual.png sits beside <name>.png" convention, but
// the convention is not what the engine reads: LoadPixelScene's second argument
// is an explicit path, and a good number of call sites pass a sibling variant's
// art. The liquid-tank holy mountains are the visible case --
// data/scripts/biomes/temple_altar_top_shared.lua sets
// `file_visual = "data/biome_impl/temple/altar_top_visual.png"` once and passes
// it for altar_top.png AND every altar_top_<liquid>.png, so the water/blood/oil/
// radioactive/lava variants are hand-painted in game while only the plain one
// was here -- and temple_wall_ending.lua does the same for altar_top_ending.
//
// Value is the basename of the art inside the SAME data/pixel_scenes/<dir>/ as
// the scene (every one of these resolves that way, and the art already ships
// because the scene it belongs to is in VISUAL_OVERLAY_SCENES). Derived by
// scanning every LoadPixelScene call and <PixelScene colors_filename> in the
// unpacked game data for a colors file whose name is not `<material>_visual.png`,
// restricted to scenes telescope ships and confirmed by matching PNG dimensions.
//
// Two known references are deliberately absent: data/biome_impl/vault/stain.png
// and stain_ceiling.png pick one of three `stain_NN_visual.png` at random per
// instance, and telescope ships neither those decals nor their art.
const SCENE_COLORS_FILE_ALIASES = {
	"coalmine/physics_01_alt": "physics_01_visual",
	"coalmine/physics_02_alt": "physics_02_visual",
	"coalmine/shop_alt": "shop_visual",
	"coalmine/shrine01_alt": "shrine01_visual",
	"coalmine/shrine02_alt": "shrine02_visual",
	"coalmine/swarm_alt": "swarm_visual",
	"coalmine/wandtrap_h_07": "wandtrap_h_06_visual",
	"crypt/room_gate_drop_b": "room_gate_drop_visual",
	"crypt/room_liquid_funnel_b": "room_liquid_funnel_visual",
	"crypt/shop_b": "shop_visual",
	"excavationsite/machine_1_alt": "machine_1_visual",
	"excavationsite/machine_2_alt": "machine_2_visual",
	"excavationsite/machine_3b_alt": "machine_3b_visual",
	"excavationsite/machine_4_alt": "machine_4_visual",
	"excavationsite/machine_5_alt": "machine_5_visual",
	"excavationsite/machine_6_alt": "machine_6_visual",
	"excavationsite/machine_7": "machine_5_visual",
	"excavationsite/machine_7_alt": "machine_5_visual",
	"excavationsite/shop_alt": "shop_visual",
	"general/bunker2": "bunker_visual",
	// The cauldron room's colors file is the one that does not follow the
	// `<name>_visual.png` convention at all: it ships as cauldron_fg.png beside
	// cauldron.png, so VISUAL_OVERLAY_SCENES -- which only knows that convention
	// -- never named the room and the art was loaded by nothing. The room is not
	// in any data.wak on disk (see data/pixel_scenes/general/cauldron*.png), so
	// the pairing is established from the art itself: cauldron_fg.png's
	// non-transparent pixels number 195,042, which is exactly the cauldron.png
	// cells that are sand (89,608) + rock_static (69,007) + rock_hard_border
	// (36,427), and it is transparent over every one of the scene's other cells.
	// That is a colors file for this material PNG and nothing else. Without it
	// the room's stone and its sand both drew material-derived, which is the
	// whole of what was reported missing.
	"general/cauldron": "cauldron_fg",
	"general/essenceroom_submerged": "essenceroom_visual",
	"pyramid/boss_limbs": "reward_visual",
	"snowcave/pipe_alt": "pipe_visual",
	"snowcave/verticalobservatory2_alt": "verticalobservatory2_visual",
	"snowcave/verticalobservatory_alt": "verticalobservatory_visual",
	"temple/altar_right_snowcastle": "altar_right_visual",
	"temple/altar_top_blood": "altar_top_visual",
	"temple/altar_top_ending": "altar_top_visual",
	"temple/altar_top_lava": "altar_top_visual",
	"temple/altar_top_oil": "altar_top_visual",
	"temple/altar_top_radioactive": "altar_top_visual",
	"temple/altar_top_water": "altar_top_visual",
};

/**
 * Basename (no extension) of the colors file the engine paints this scene with,
 * inside data/pixel_scenes/<dir>/, or null when the scene has none.
 */
export function sceneColorsFileName(dir, name) {
	const id = `${dir}/${name}`;
	if (SCENE_COLORS_FILE_ALIASES[id]) return SCENE_COLORS_FILE_ALIASES[id];
	return VISUAL_OVERLAY_SCENES.has(id) ? `${name}_visual` : null;
}

// This was originally constant but it sometimes needs to be cleared to regenerate the cache...
export let PIXEL_SCENE_DATA = {};
let pixelSceneLoadGen = 0;
export let PIXEL_SCENE_SPAWN_DATA = {}; // Populated during the prescan of pixel scenes, keyed by biome and scene name, used for looking up spawn points during generation without needing to access the image data again
// ---------------------------------------------------------------------------
// Pixel scene bitmap cache (PERF_PLAN Step 1)
//
// Each recolored variant is uploaded once as an ImageBitmap plus a mip chain
// (1/2 .. 1/16). The draw site picks a level from the camera zoom, so a zoomed-out
// frame blits a handful of pixels per scene instead of a full-resolution image, which
// is what made drawing them at low zoom expensive enough to gate off entirely.
//
// The raw recolored Uint8Array is released once its bitmap exists (the overlay worker
// recolors from the untouched base image, so any variant can be produced again). Entries
// are evicted least-recently-drawn first over a byte budget; an evicted variant is
// re-requested from the overlay worker through the rebuild hook below.
// ---------------------------------------------------------------------------
export const PIXEL_SCENE_MAX_MIP = 4; // 1/2, 1/4, 1/8, 1/16
const PIXEL_SCENE_BITMAP_CACHE = new Map(); // `${key}/${variantKey}` -> entry
let pixelSceneCacheBytes = 0;
let pixelSceneDrawTick = 0;

// Left in place of a released Uint8Array so the "do we already have this variant?" checks
// in overlay_manager / world_manager don't re-request it while its bitmap is still alive.
const VARIANT_RELEASED = { released: true };

// ---------------------------------------------------------------------------
// Bitmaps are BUILT IN THE OVERLAY WORKER, never on this thread.
//
// Turning a scene's pixels into a drawable used to happen synchronously on the
// first draw that needed it: recolor -> visual-art overlay -> putImageData ->
// transferToImageBitmap, then a GPU readback + JS halving for every mip level,
// and for a textured instance a full per-pixel material build on top. That was
// 30-110 ms for one large scene scrolling into view and a 50 ms frame at the
// overview zoom -- the worst frames the renderer had once the tooltip readback
// was gone. Now a cache miss posts a BUILD_SCENE_BITMAPS request (through the
// hook overlay_manager registers below) and returns nothing to draw; the worker
// answers with the finished ImageBitmap chain, which lands here through
// putPixelSceneBitmaps(). The scene is missing for the frames in between, which
// drawNow hides by warming scenes in a margin around the view before they are
// visible (warmPixelScene).
//
// A textured instance that is still being built draws its FLAT variant meanwhile
// (same key without the position), so crossing the texture zoom shows a flat
// scene that sharpens rather than a hole.
// ---------------------------------------------------------------------------
let sceneBitmapRequester = null;   // (request, sceneData) => void, set by overlay_manager
const pendingSceneBitmaps = new Map();   // cacheKey -> build kind (in flight at the worker)
const pendingSceneWeight = new Map();    // cacheKey -> share of an in-flight slot (sceneBuildWeight)
// Running sums of pendingSceneWeight per build kind. A requery asks for every
// missing scene near the view, most of them past the cap; summing the pending
// map on each ask made a frame's cost grow with (missing scenes x in flight).
const inflightWeight = { flat: 0, resolved: 0, textured: 0 };
// Cumulative, for the Render HUD. A refetch is a request for a key that was
// already delivered once -- evicted and wanted back, the signature of thrash.
const sceneBitmapCounters = { requests: 0, refetches: 0, evictions: 0, failures: 0 };
// Worker build time per build kind (flat / resolved / textured): { n, ms }.
const sceneBuildTime = {};
const deliveredSceneBitmaps = new Set();
// Bumped whenever the cache is cleared, so a reply for the old contents is dropped.
let sceneBitmapEpoch = 0;
// In-flight caps. The worker is FIFO and cannot cancel: with no cap a pan at the
// textured zoom would queue every instance it passed over (each a 30-110 ms
// build) ahead of the ones actually on screen. Misses past the cap are simply
// asked again on the next draw, which is what keeps requests tracking the view.
const MAX_INFLIGHT_TEXTURED = 6;
// The shared builds are one per scene variant, a few hundred for everything a
// seed places, and an overview wants all of them: the cap is there for a
// runaway, not to pace them. (At 48 an overview's builds went out in rounds,
// each waiting for a draw after the last had landed, and took three times as
// long as the workers needed.)
const MAX_INFLIGHT_FLAT = 512;
// Zoomed-out per-instance builds (see pixelSceneEntry): ~4^-level of a textured
// build's chooser work, so more of them can be in flight.
const MAX_INFLIGHT_RESOLVED = 16;
// The caps above count a scene of at least SLOT_AREA pixels as one slot and a
// smaller one as its share: a build costs about its pixel count, and the vault's
// hundreds of 10x10 pipe pieces took a full textured slot each -- six at a
// time, one round trip per six, ~9 s to fill a view that changes nothing
// visible. MAX_INFLIGHT_COUNT still bounds the messages in flight.
const SLOT_AREA = 128 * 128;
const MAX_INFLIGHT_COUNT = 640;
function sceneBuildWeight(data) {
	return Math.min(1, (data.width * data.height) / SLOT_AREA);
}

export function setPixelSceneBitmapRequester(fn) {
	sceneBitmapRequester = fn;
}

/** Scene bitmaps asked of the worker and not back yet (the harness waits on it). */
export function pendingPixelSceneBitmaps() {
	return pendingSceneBitmaps.size + neededSceneMaterials;
}

// `level` 0 with `textured` is the full-res textured build; level > 0 is the
// zoomed-out per-instance build at that level.
function requestSceneBitmaps(pixelScene, cacheKey, textured, level = 0) {
	if (!sceneBitmapRequester || pendingSceneBitmaps.has(cacheKey)) return;
	const data = PIXEL_SCENE_DATA[pixelScene.key];
	if (!data) return;
	const kind = !textured ? 'flat' : level > 0 ? 'resolved' : 'textured';
	if (pendingSceneBitmaps.size >= MAX_INFLIGHT_COUNT) return;
	const weight = sceneBuildWeight(data);
	const inflight = inflightWeight[kind];
	// Render Everything asks for a textured build of every visible instance at
	// any zoom (~1150 at the overview); at the normal cap that trickles in over
	// a minute, so let the worker run flat out there. Its whole point is a
	// zoom-independent frame, not a responsive one.
	const texturedCap = appSettings.renderEverything ? 32 : MAX_INFLIGHT_TEXTURED;
	const cap = kind === 'flat' ? MAX_INFLIGHT_FLAT : kind === 'resolved' ? MAX_INFLIGHT_RESOLVED : texturedCap;
	if (inflight + weight > cap && inflight > 0) return;
	pendingSceneBitmaps.set(cacheKey, kind);
	pendingSceneWeight.set(cacheKey, weight);
	inflightWeight[kind] += weight;
	sceneBitmapCounters.requests++;
	if (deliveredSceneBitmaps.has(cacheKey)) sceneBitmapCounters.refetches++;
	const maxLevel = sceneMaxMipLevel(data.width, data.height);
	const erase = !textured && cacheKey.endsWith(FLAT_ERASE_SUFFIX);
	sceneBitmapRequester({
		cmd: 'BUILD_SCENE_BITMAPS',
		epoch: sceneBitmapEpoch,
		cacheKey,
		key: pixelScene.key,
		variantKey: pixelScene.variantKey || '',
		x: pixelScene.x,
		y: pixelScene.y,
		textured,
		level: textured ? level : 0,
		erase,
		texturedAlphas: erase ? texturedFlatAlphas() : null,
		maxLevel,
		// With material textures on, the shared build is what scenes draw from
		// only past SCENE_INSTANCE_MAX_LEVEL -- nearer than that they are
		// textured per placement (on the GPU from a material map, or from an
		// instance build), and this is at most a stand-in. Its finer levels are
		// four fifths of its pixels and of the bitmaps the worker would make,
		// so they are not made.
		keepFrom: erase ? Math.min(SCENE_INSTANCE_MAX_LEVEL + 1, maxLevel) : 0,
	}, data);
}

/**
 * Accepts a finished bitmap chain from the worker (overlay_manager routes the
 * SCENE_BITMAPS reply here). Returns true when something new is drawable.
 */
// Bumped whenever a drawable scene appears or disappears, so a caller that
// composites many scenes into one bitmap (app.js sceneBake) knows to rebuild.
let sceneBitmapVersion = 0;
export function pixelSceneBitmapVersion() {
	return sceneBitmapVersion;
}

export function putPixelSceneBitmaps(msg) {
	const kind = pendingSceneBitmaps.get(msg.cacheKey);
	if (kind && msg.buildMs != null) {
		const t = sceneBuildTime[kind] ??= { n: 0, ms: 0 };
		t.n++; t.ms += msg.buildMs;
	}
	if (kind) inflightWeight[kind] = Math.max(0, inflightWeight[kind] - pendingSceneWeight.get(msg.cacheKey));
	pendingSceneBitmaps.delete(msg.cacheKey);
	pendingSceneWeight.delete(msg.cacheKey);
	// Fractional weights: let no rounding residue outlive the last build.
	if (!pendingSceneBitmaps.size) inflightWeight.flat = inflightWeight.resolved = inflightWeight.textured = 0;
	sceneBitmapVersion++;
	const bitmaps = [...(msg.levels || []), ...(msg.airMasks || [])].filter(Boolean);
	// A zoomed-out build leaves the levels finer than its own null.
	if (msg.epoch !== sceneBitmapEpoch || !msg.levels || !msg.levels.some(Boolean)) {
		for (const b of bitmaps) b.close?.();
		if (msg.epoch === sceneBitmapEpoch) logSceneBitmapFailure(msg);
		return false;
	}
	const old = PIXEL_SCENE_BITMAP_CACHE.get(msg.cacheKey);
	if (old) releasePixelSceneEntry(old);
	const entry = {
		cacheKey: msg.cacheKey,
		width: msg.width,
		height: msg.height,
		levels: new Array(PIXEL_SCENE_MAX_MIP + 1).fill(null),
		maxLevel: msg.levels.length - 1,
		minLevel: Math.max(0, msg.levels.findIndex(Boolean)),
		airMasks: msg.airMasks || [],   // per level, alongside `levels`
		bytes: 0,
		// As recent as anything drawn: it was asked for because it is on (or
		// about to be on) screen. At 0 it was the LRU minimum, so with the cache
		// full every arrival was evicted by the next draw before it was ever
		// drawn, and re-requested -- a build loop that never showed the scene.
		used: ++pixelSceneDrawTick,
	};
	for (let l = 0; l < msg.levels.length; l++) if (msg.levels[l]) addPixelSceneBitmap(entry, l, msg.levels[l]);
	for (const m of entry.airMasks) {
		if (!m) continue;
		const bytes = m.width * m.height * 4;
		entry.bytes += bytes;
		pixelSceneCacheBytes += bytes;
	}
	PIXEL_SCENE_BITMAP_CACHE.set(entry.cacheKey, entry);
	deliveredSceneBitmaps.add(entry.cacheKey);
	// The bitmap is now the only copy this thread needs of a biome-recolored
	// variant: material lookups (utils.js) read the pre-biome variants, which are
	// left alone, and the worker rebuilds any variant from the base image.
	const data = PIXEL_SCENE_DATA[msg.key];
	if (!msg.textured && data?.variants && msg.variantKey.includes('biome=')
		&& ArrayBuffer.isView(data.variants[msg.variantKey])) {
		data.variants[msg.variantKey] = VARIANT_RELEASED;
	}
	return true;
}

// A failed build caches nothing, so the next draw asks again: a scene that
// always fails is re-requested every frame it is in view. Count per key so
// that loop is visible in the console without flooding it.
const sceneBitmapFailures = new Map();   // cacheKey -> count
function logSceneBitmapFailure(msg) {
	const n = (sceneBitmapFailures.get(msg.cacheKey) || 0) + 1;
	sceneBitmapFailures.set(msg.cacheKey, n);
	sceneBitmapCounters.failures++;
	if (n <= 3 || n % 50 === 0) {
		const data = PIXEL_SCENE_DATA[msg.key];
		console.error(`[scene bitmaps] worker returned no bitmap for ${msg.cacheKey}${msg.textured ? ' (tex)' : ''}`
			+ ` (failure #${n}${n > 1 ? ', re-request loop' : ''}): ${msg.failReason ?? 'no reason given'}`,
			{ msg, mainThreadImg: data?.imgElement?.constructor?.name, variant: data?.variants?.[msg.variantKey],
				epoch: sceneBitmapEpoch, pending: pendingSceneBitmaps.size, cacheEntries: PIXEL_SCENE_BITMAP_CACHE.size });
	}
}

/** Halving stops once either axis would round to nothing. */
export function sceneMaxMipLevel(width, height) {
	return Math.min(PIXEL_SCENE_MAX_MIP, Math.floor(Math.log2(Math.max(1, Math.min(width, height)))));
}

export function getPixelSceneCacheStats() {
	let pendingTextured = 0;
	for (const k of pendingSceneBitmaps.values()) if (k !== 'flat') pendingTextured++;
	return {
		entries: PIXEL_SCENE_BITMAP_CACHE.size, bytes: pixelSceneCacheBytes, budgetBytes: sceneBitmapBudgetBytes(),
		pending: pendingSceneBitmaps.size, pendingTextured, ...sceneBitmapCounters,
		buildTime: structuredClone(sceneBuildTime),
		materialMaps: SCENE_MATERIAL_MAPS.size, pendingMaterialMaps: pendingSceneMaterials.size,
	};
}

function releasePixelSceneEntry(entry) {
	for (const bitmap of entry.levels) {
		if (bitmap) bitmap.close();
	}
	for (const m of entry.airMasks) if (m) m.close();
	pixelSceneCacheBytes -= entry.bytes;
	PIXEL_SCENE_BITMAP_CACHE.delete(entry.cacheKey);
	// No version bump: a bake that already holds the evicted scene's pixels
	// stays correct, and bumping here made every eviction a full rebuild.
}

export function clearPixelSceneBitmapCache() {
	for (const entry of [...PIXEL_SCENE_BITMAP_CACHE.values()]) releasePixelSceneEntry(entry);
	pixelSceneCacheBytes = 0;
	pendingSceneBitmaps.clear();
	pendingSceneWeight.clear();
	inflightWeight.flat = inflightWeight.resolved = inflightWeight.textured = 0;
	deliveredSceneBitmaps.clear();
	SCENE_MATERIAL_MAPS.clear();
	pendingSceneMaterials.clear();
	failedSceneMaterials.clear();
	inflightMaterialWeight = 0;
	neededSceneMaterials = 0;
	sceneBitmapEpoch++;
	sceneBitmapVersion++;
}

// ---------------------------------------------------------------------------
// Scene material maps (the GL scene pass, gl/scene_renderer.js)
//
// With material textures on, the GL pass does not draw a scene from a bitmap
// built for the place it landed. It uploads the scene ONCE per variant as what
// each pixel IS -- untouched, air, the biome's density class, a material, a
// plain color -- plus its colors-file art (buildSceneMaterialMap), and the
// fragment shader gives every cell its color from the cell's own world
// position, with the code the terrain under it runs. So a variant is built
// once however many times it is placed, the build is a single pass over the
// PNG with no band chooser in it, and nothing is rebuilt per zoom level:
// there is no coarser picture to show first and swap out later.
//
// A map is asked of the overlay worker like a bitmap, lands here as bytes, and
// is handed to the renderer, which releases the bytes once they are on the GPU.
// Requests made ahead of need (`warm`) are not counted as pending work: a
// frame that does not draw the scene is not waiting for it.
// ---------------------------------------------------------------------------
const SCENE_MATERIAL_MAPS = new Map();     // flat key -> { cacheKey, width, height, layers, data, erase }
const pendingSceneMaterials = new Map();   // flat key -> { warm, weight }
// Keys the worker could not build (the PNG did not decode): not asked again,
// or every frame that wants the scene would ask.
const failedSceneMaterials = new Set();
let inflightMaterialWeight = 0;
let neededSceneMaterials = 0;              // pending requests that a frame is waiting for
// A map build is a decode and one classifying pass, a fraction of a bitmap
// build, so many can be in flight; weighted like the bitmap caps (SLOT_AREA).
const MAX_INFLIGHT_MATERIALS = 32;

/** The key a scene's material map is cached under: its variant, no position. */
export function sceneMaterialKey(pixelScene) {
	return sceneKeys(pixelScene).flat;
}

/** The landed map for a key, or undefined. `data` is null once a renderer took the bytes. */
export function landedSceneMaterialMap(cacheKey) {
	return SCENE_MATERIAL_MAPS.get(cacheKey);
}

/** True when the worker has answered that it cannot build this map. */
export function sceneMaterialMapFailed(cacheKey) {
	return failedSceneMaterials.has(cacheKey);
}

/**
 * Asks the worker for a scene's material map.
 * @param {boolean} warm  asked ahead of need; a later call without it marks
 *        the request in flight as needed
 * @returns {'landed'|'pending'|'refused'|'failed'}  refused: too many in
 *          flight, ask again
 */
export function requestSceneMaterialMap(pixelScene, warm = false) {
	const cacheKey = sceneKeys(pixelScene).flat;
	if (SCENE_MATERIAL_MAPS.has(cacheKey)) return 'landed';
	if (failedSceneMaterials.has(cacheKey)) return 'failed';
	const pending = pendingSceneMaterials.get(cacheKey);
	if (pending) {
		if (pending.warm && !warm) { pending.warm = false; neededSceneMaterials++; }
		return 'pending';
	}
	const data = PIXEL_SCENE_DATA[pixelScene.key];
	if (!sceneBitmapRequester || !data) return 'refused';
	if (pendingSceneMaterials.size >= MAX_INFLIGHT_COUNT) return 'refused';
	const weight = sceneBuildWeight(data);
	if (inflightMaterialWeight + weight > MAX_INFLIGHT_MATERIALS && inflightMaterialWeight > 0) return 'refused';
	pendingSceneMaterials.set(cacheKey, { warm, weight });
	inflightMaterialWeight += weight;
	if (!warm) neededSceneMaterials++;
	sceneBitmapCounters.requests++;
	sceneBitmapRequester({
		cmd: 'BUILD_SCENE_MATERIALS',
		epoch: sceneBitmapEpoch,
		cacheKey,
		key: pixelScene.key,
		variantKey: pixelScene.variantKey || '',
		texturedAlphas: texturedFlatAlphas(),
	}, data);
	return 'pending';
}

/** Accepts a SCENE_MATERIALS reply. Returns true when a map became available. */
export function putSceneMaterialMap(msg) {
	const pending = pendingSceneMaterials.get(msg.cacheKey);
	if (pending) {
		inflightMaterialWeight = Math.max(0, inflightMaterialWeight - pending.weight);
		if (!pending.warm) neededSceneMaterials--;
		pendingSceneMaterials.delete(msg.cacheKey);
		if (msg.buildMs != null) {
			const t = sceneBuildTime.materials ??= { n: 0, ms: 0 };
			t.n++; t.ms += msg.buildMs;
		}
	}
	if (!pendingSceneMaterials.size) inflightMaterialWeight = 0;
	// A map nobody is drawing yet changes no frame: only one a frame asked for
	// makes the scene pass look again.
	if (!pending || !pending.warm) sceneBitmapVersion++;
	if (msg.epoch !== sceneBitmapEpoch) return false;
	if (!msg.data) {
		failedSceneMaterials.add(msg.cacheKey);
		logSceneBitmapFailure(msg);
		return false;
	}
	SCENE_MATERIAL_MAPS.set(msg.cacheKey, {
		cacheKey: msg.cacheKey, width: msg.width, height: msg.height, layers: msg.layers, data: msg.data, erase: msg.erase,
	});
	return true;
}

/** The renderer has the map on the GPU: drop this thread's copy of the bytes. */
export function releaseSceneMaterialBytes(cacheKey) {
	const entry = SCENE_MATERIAL_MAPS.get(cacheKey);
	if (entry) entry.data = null;
}

/** The renderer no longer holds the map (evicted, context lost): the next ask rebuilds it. */
export function forgetSceneMaterialMap(cacheKey) {
	SCENE_MATERIAL_MAPS.delete(cacheKey);
}

// Under Render Everything the full-res textured instances of a whole overview
// view (~270 MB, more at 4K) do not fit the default budget, and evicting them
// only re-requests them next frame: the cache would thrash and never converge.
// Give the debug mode room instead.
function sceneBitmapBudgetBytes() {
	const budgetMB = appSettings.renderEverything
		? Math.max(appSettings.pixelSceneBitmapBudgetMB || 1024, 2048) : (appSettings.pixelSceneBitmapBudgetMB || 1024);
	return budgetMB * 1024 * 1024;
}

function evictPixelSceneBitmaps(keep) {
	const budget = sceneBitmapBudgetBytes();
	if (pixelSceneCacheBytes <= budget) return;
	const entries = [...PIXEL_SCENE_BITMAP_CACHE.values()].sort((a, b) => a.used - b.used);
	for (const entry of entries) {
		if (pixelSceneCacheBytes <= budget) break;
		if (entry === keep) continue; // never drop the one we are about to draw
		releasePixelSceneEntry(entry);
		sceneBitmapCounters.evictions++;
	}
}

function addPixelSceneBitmap(entry, level, bitmap) {
	const bytes = bitmap.width * bitmap.height * 4;
	entry.levels[level] = bitmap;
	entry.bytes += bytes;
	pixelSceneCacheBytes += bytes;
}

/**
 * The engine-faithful pixels for one stamped scene instance, or null when the
 * atlas/band tables are not loaded (or scene texturing is off).
 *
 * Runs the variant key the same way the overlay worker does -- material
 * substitutions first, in wang color space, so the texturing step still sees wang
 * colors -- but from the untouched base image and with the instance's own world
 * position, which is what the texture sampling needs.
 */
export function buildTexturedScenePixels(pixelScene, pixelSceneData, ignoreSettings = false, level = 0) {
	// The worker builds on the main thread's say-so (it decided `textured` from
	// its own settings); its copy of the settings can lag a SYNC behind.
	if (!(ignoreSettings ? sceneTextureModules?.atlas.getMaterialAtlas() : sceneTextureAtlas())) return null;
	let pixels = pixelSceneData.imgElement;
	let biome = 'general';
	for (const part of (pixelScene.variantKey || '').split('&')) {
		const eq = part.indexOf('=');
		if (eq < 0) continue;
		if (part.slice(0, eq) === 'biome') biome = part.slice(eq + 1);
		else pixels = recolorPixelScene(pixels, parseInt(part.slice(0, eq), 16), parseInt(part.slice(eq + 1), 16));
	}
	return texturePixelSceneForBiome(pixelSceneData.name, pixels, pixelSceneData.width, pixelSceneData.height,
		biome, pixelScene.x, pixelScene.y, level);
}

// Source-over blend of a scene's visual-art override onto a COPY of its built
// pixels, restricted to painted-cell (alpha > 0) destination pixels. The visual may
// be smaller than the scene (hall_b/hall_br); both are anchored top-left.
export function overlayVisualArt(raw, width, height, art) {
	const out = new Uint8Array(raw.length);
	out.set(raw);
	const w = Math.min(width, art.width), h = Math.min(height, art.height);
	const s = art.data;
	for (let y = 0; y < h; y++) {
		for (let x = 0; x < w; x++) {
			const si = (y * art.width + x) * 4;
			const a = s[si + 3];
			if (a === 0) continue;
			const di = (y * width + x) * 4;
			if (out[di + 3] === 0) continue; // no cell here, art places none either
			if (a === 255) {
				// The art is the cell's color outright, so a translucent material
				// (glass texels are alpha 183) takes the art's opacity with it.
				out[di] = s[si]; out[di + 1] = s[si + 1]; out[di + 2] = s[si + 2]; out[di + 3] = 255;
			} else {
				const inv = 255 - a;
				out[di] = (s[si] * a + out[di] * inv + 127) / 255 | 0;
				out[di + 1] = (s[si + 1] * a + out[di + 1] * inv + 127) / 255 | 0;
				out[di + 2] = (s[si + 2] * a + out[di + 2] * inv + 127) / 255 | 0;
			}
		}
	}
	return out;
}

// Halve an image the way a cutout sprite wants to be halved: point sampling, but never
// losing coverage.
//
// Scene alpha is binary - a pixel is either painted material or air - and the seam
// between a scene and whatever it sits on is often one pixel thin. Averaging the 2x2
// block (a box filter) turns that seam into partial alpha, so the dark world behind
// shows through as a hairline crack along the scene edge; plain point sampling drops the
// seam outright and opens the same crack, wider. So the output takes the point sample
// (the block's lower-right pixel, which is what a nearest-neighbour downscale picks) and
// falls back to whichever of the other three is painted when that one is air. Painted
// area can only grow, never perforate, and alpha stays binary all the way down.
//
// Only the coverage is point-sampled: the color is the alpha-weighted average of the
// block's painted pixels. A point-sampled color bakes moire into every level of a
// regular pattern (brick walls), and averaging painted pixels alone opens no crack.
export function halveWithoutHoles(img) {
	const sw = img.width, sh = img.height, s = img.data;
	// Round up, so an odd-sized level keeps its last row/column instead of dropping it -
	// that trailing row is exactly the kind of one-pixel edge this reduction exists to keep.
	const w = Math.max(1, Math.ceil(sw / 2)), h = Math.max(1, Math.ceil(sh / 2));
	const out = new ImageData(w, h);
	const d = out.data;
	for (let y = 0; y < h; y++) {
		const rowLo = Math.min(y * 2 + 1, sh - 1) * sw;
		const rowHi = y * 2 * sw;
		for (let x = 0; x < w; x++) {
			const colLo = Math.min(x * 2 + 1, sw - 1);
			const colHi = x * 2;
			let p = (rowLo + colLo) * 4;
			const b = (rowLo + colHi) * 4, c = (rowHi + colLo) * 4, e = (rowHi + colHi) * 4;
			if (s[p + 3] !== 255) {
				if (s[b + 3] > s[p + 3]) p = b;
				if (s[c + 3] > s[p + 3]) p = c;
				if (s[e + 3] > s[p + 3]) p = e;
			}
			const o = (y * w + x) * 4;
			// An odd trailing row/column reuses a pixel; counting it twice is harmless.
			const wa = s[p + 3] + s[b + 3] + s[c + 3] + s[e + 3];
			if (wa === 0) {
				d[o] = s[p]; d[o + 1] = s[p + 1]; d[o + 2] = s[p + 2];
			} else {
				const pa = s[p + 3], ba = s[b + 3], ca = s[c + 3], ea = s[e + 3];
				for (let k = 0; k < 3; k++) {
					d[o + k] = Math.round((s[p + k] * pa + s[b + k] * ba + s[c + k] * ca + s[e + k] * ea) / wa);
				}
			}
			d[o + 3] = s[p + 3];
		}
	}
	return out;
}

// Largest mip whose resolution still meets the on-screen resolution: at camera zoom z one
// world pixel covers z screen pixels, so a 1/2^L bitmap is enough while 2^-L >= z.
// Anything at or above 1:1 draws the native image, exactly as before.
export function pixelSceneMipLevel(z) {
	if (!(z > 0) || z >= 1) return 0;
	const level = Math.floor(Math.log2(1 / z));
	return level > PIXEL_SCENE_MAX_MIP ? PIXEL_SCENE_MAX_MIP : level;
}

export function injectPixelSceneSpawnData(cachedData) {
	PIXEL_SCENE_SPAWN_DATA = cachedData;
}

export function injectPixelSceneData(cachedData) {
	// A re-sync of the same load (every seed change) keeps the pixels this
	// thread already decoded; a reload (e.g. clearSpawnPixels toggled) does not.
	for (const [key, rec] of Object.entries(cachedData)) {
		const old = PIXEL_SCENE_DATA[key];
		if (old?.loadGen === undefined || old.loadGen !== rec.loadGen) continue;
		rec.imgElement ??= old.imgElement;
		rec.visualArt ??= old.visualArt;
	}
	PIXEL_SCENE_DATA = cachedData;
}

// How close a biome's background color may sit to its fill color before air
// painted with it would be indistinguishable from the surrounding solid rock,
// and how far to darken the fill when that happens.
const AIR_OVER_FILL_TOLERANCE = 24;
const AIR_OVER_FILL_DARKEN = 0.45;

const SCENES_TO_NOT_RECOLOR = ["wand_altar", "wand_altar_vault", "potion_altar", "potion_altar_vault"]; // It would be a waste to recolor these for every biome
const PIXEL_SCENE_AIR_TRANSPARENCY_EXCEPTIONS = {
	"the_end_shop": 0xff,
	"cavern": 0xff,
	"friendroom": 0xff,
	"eyespot": 0xff,
	"altar_snowcastle_capsule": 0xff,
	"altar_vault_capsule": 0xff,
	"altar_snowcave_capsule": 0xff,
	// These two look good when solid if there isn't custom art, but bad if there is. Not really sure what the best option is
	//"altar_top": 0xff,
	//"altar_top_ending": 0xff,
	"tower_start": 0xff,
	"solid_wall_hidden_cavern": 0xff,
	"watercave_layout_1": 0xff,
	"watercave_layout_2": 0xff,
	"watercave_layout_3": 0xff,
	"watercave_layout_4": 0xff,
	"watercave_layout_5": 0xff,
	// Otherwise assume transparent?
}

export async function reloadPixelSceneCache() {
	clearPixelSceneBitmapCache();
	PIXEL_SCENE_DATA = {};
	pixelSceneLoadGen++;
	await loadPixelSceneData();
}

// With material textures on, flat (zoomed-out) builds erase the terrain under
// their air and liquids like the textured build does (eraseFlatScenePixels). The
// toggle does not rebuild bitmaps, so the two flat flavors are cached apart.
const FLAT_ERASE_SUFFIX = '~erase';

// [wang 0xRRGGBB, alpha] for every textured material whose flat alpha (mean
// texel alpha) is below 255: ice, glass... The worker never loads the atlas, so
// the erase-aware flat build gets them with each request.
let texturedFlatAlphaList = null;
function texturedFlatAlphas() {
	if (texturedFlatAlphaList) return texturedFlatAlphaList;
	const atlas = sceneTextureAtlas();
	if (!atlas) return [];
	texturedFlatAlphaList = [];
	for (const m of MATERIAL_DATA) {
		if (!m.texture || !m.wang) continue;
		const a = sceneTextureModules.atlas.materialFlatAlpha(atlas, m.name);
		if (a < 255) texturedFlatAlphaList.push([parseInt(m.wang, 16) & 0xffffff, a]);
	}
	return texturedFlatAlphaList;
}
// A scene's cache keys, built once per placement: the GL scene pass asks for
// every missing scene near the view on each requery (~every 70 ms while builds
// land), and rebuilding these strings each time was most of that frame cost.
const sceneKeyMemo = new WeakMap();   // placement -> { flat, flatErase, inst: [] }
function sceneKeys(pixelScene) {
	let m = sceneKeyMemo.get(pixelScene);
	if (!m) {
		const flat = `${pixelScene.key}/${pixelScene.variantKey || ''}`;
		m = { flat, flatErase: flat + FLAT_ERASE_SUFFIX, inst: [`${flat}@${pixelScene.x},${pixelScene.y}`], mat: flat + '#mat' };
		sceneKeyMemo.set(pixelScene, m);
	}
	return m;
}
/** The GL scene pass' slot key for a scene's material map. */
export function sceneMaterialSlotKey(pixelScene) {
	return sceneKeys(pixelScene).mat;
}
function flatCacheKey(pixelScene) {
	const m = sceneKeys(pixelScene);
	return sceneTextureAtlas() ? m.flatErase : m.flat;
}
function texturedCacheKey(pixelScene, level = 0) {
	const inst = sceneKeys(pixelScene).inst;
	return inst[level] ??= `${inst[0]}~${level}`;
}
// The level a zoomed-out instance is built at: the requested one, clamped to the
// scene's own mip chain (getPixelSceneDrawable clamps the same way).
function resolvedBuildLevel(pixelScene, level) {
	return Math.min(level, sceneMaxMipLevel(pixelScene.width, pixelScene.height));
}
// Past this mip level scenes draw the shared per-variant build instead of one
// per instance. The two differ only in where the band chooser's rare picks and
// noise veins land. test/lod_diff.mjs: sharing from level 3 (1/8) cost up to
// +0.25 mean dE (lavalake), from level 4 (1/16) at most +0.1 -- and the zoomed-
// out overview is where a thousand-plus instances would each need a build.
export const SCENE_INSTANCE_MAX_LEVEL = 2;
// The shared build's density class samples the band chooser on a 2^this grid.
export const SCENE_SHARED_SAMPLE_LEVEL = 2;
function sceneUsesInstanceBuild(level) {
	return level <= SCENE_INSTANCE_MAX_LEVEL;
}

// Returns the bitmap to draw for this scene at the requested mip level (0 = native), or
// null when its recolored pixels aren't available yet. The caller always draws it into
// the scene's full-resolution world rectangle, so the level only changes sampling.
function pixelSceneEntry(pixelScene, level = 0, warmOnly = false) {
	// With material textures on, a scene's pixels come from its material textures
	// sampled at ABSOLUTE world coordinates, and its density class from the band
	// chooser at absolute coordinates, so two instances of the same variant do not
	// look alike: the cache key has to carry the instance's position.
	//
	// At level 0 that is the full-res textured build. Zoomed out to
	// SCENE_INSTANCE_MAX_LEVEL it is the same build at the level's resolution
	// (texturePixelSceneForBiome `level`): block-sampled band choices and each
	// material's mean texel color, what the textured one averages to. Further
	// out, and as the stand-in for either while it is being built, every instance
	// of a variant shares one build of the same kind made at a fixed origin
	// (flatCacheKey). Without the atlas (material textures off) every level
	// shares the plain flat variant.
	const textured = !!sceneTextureAtlas();
	const flatKey = flatCacheKey(pixelScene);
	if (!textured || !sceneUsesInstanceBuild(level)) {
		const flat = PIXEL_SCENE_BITMAP_CACHE.get(flatKey);
		if (flat) return flat;
		requestSceneBitmaps(pixelScene, flatKey, false);
		return null;
	}
	const buildLevel = resolvedBuildLevel(pixelScene, level);
	const cacheKey = texturedCacheKey(pixelScene, buildLevel);
	const entry = PIXEL_SCENE_BITMAP_CACHE.get(cacheKey);
	if (entry) return entry;
	// The full-res textured build is expensive, so it is not warmed ahead of the
	// view (only its shared stand-in is); the zoomed-out ones are cheap enough to
	// be. Warming draws nothing, so it needs no stand-in search.
	if (warmOnly) {
		if (buildLevel > 0) requestSceneBitmaps(pixelScene, cacheKey, true, buildLevel);
		else if (!PIXEL_SCENE_BITMAP_CACHE.has(flatKey)) requestSceneBitmaps(pixelScene, flatKey, false);
		return null;
	}
	requestSceneBitmaps(pixelScene, cacheKey, true, buildLevel);
	// Stand-in while it is built: this instance's build at the nearest other
	// level already in the cache (a zoom across a level boundary keeps the same
	// pixels, just resampled), else the shared variant build.
	const maxLevel = Math.min(SCENE_INSTANCE_MAX_LEVEL, sceneMaxMipLevel(pixelScene.width, pixelScene.height));
	for (let d = 1; d <= maxLevel; d++) {
		const below = buildLevel - d, above = buildLevel + d;
		const near = (below >= 0 && PIXEL_SCENE_BITMAP_CACHE.get(texturedCacheKey(pixelScene, below)))
			|| (above <= maxLevel && PIXEL_SCENE_BITMAP_CACHE.get(texturedCacheKey(pixelScene, above)));
		if (near) return near;
	}
	const flat = PIXEL_SCENE_BITMAP_CACHE.get(flatKey);
	if (flat) return flat;
	requestSceneBitmaps(pixelScene, flatKey, false);
	return null;
}

/**
 * Asks the worker for a scene's bitmaps without drawing it, so a scene about to
 * scroll into view is ready when it does. Textured instances are not warmed.
 */
export function warmPixelScene(pixelScene, level = 0) {
	pixelSceneEntry(pixelScene, level, true);
}

export function getPixelSceneCanvas(pixelScene, level = 0) {
	return getPixelSceneDrawable(pixelScene, level)?.bitmap ?? null;
}

/**
 * getPixelSceneCanvas plus which cache entry answered: `cacheKey` names the
 * pixels (the flat stand-in of a textured instance has the flat key), and
 * `airMask` is that entry's FORCE AIR mask. The GL scene pass keys its atlas
 * slots on `cacheKey`, so it can tell the stand-in from the real thing.
 */
export function getPixelSceneDrawable(pixelScene, level = 0) {
	const entry = pixelSceneEntry(pixelScene, level);
	if (!entry) return null;
	entry.used = ++pixelSceneDrawTick;
	// A zoomed-out build has no levels finer than its own; standing in for a
	// finer level it draws its finest.
	const wanted = Math.max(entry.minLevel, level > entry.maxLevel ? entry.maxLevel : level);
	const bitmap = entry.levels[wanted];
	evictPixelSceneBitmaps(entry);
	return { cacheKey: entry.cacheKey, bitmap, airMask: entry.airMasks[wanted] ?? null };
}

/**
 * The cache keys a scene draws from at `level`, best first, without touching
 * the cache: the per-instance key for that level then its flat stand-in with
 * material textures on, else just the flat key. Same rule as
 * pixelSceneEntry.
 */
export function pixelSceneCacheKeys(pixelScene, level = 0) {
	const flatKey = flatCacheKey(pixelScene);
	if (pixelScenesTexturedAt(level)) return [texturedCacheKey(pixelScene, resolvedBuildLevel(pixelScene, level)), flatKey];
	return [flatKey];
}

/** Whether scenes at `level` draw per-instance builds (see pixelSceneEntry). */
export function pixelScenesTexturedAt(level) {
	return sceneUsesInstanceBuild(level) && !!sceneTextureAtlas();
}

/** Bumped when the cache is cleared: pixels under an old key may have changed. */
export function pixelSceneCacheEpoch() {
	return sceneBitmapEpoch;
}

/**
 * The scene's FORCE AIR mask, to be drawn with `destination-out` before the
 * scene itself, or null when it has none.
 *
 * Air in a scene PNG (#000042) is not a color the game paints, it is an
 * instruction to erase whatever terrain the chunk generated there. Translucent
 * materials erase too, then blend over the background. Only builds made with
 * material textures on carry masks (the textured build, and the flat build via
 * eraseFlatScenePixels): with textures off the flat recolor keeps its old
 * "paint the background color opaque over a fill biome" approximation, so that
 * path renders exactly as before.
 *
 * Cheap to call right after getPixelSceneCanvas() -- pass the same level so it
 * hits the same cache entry and gets the mask of the same mip level.
 */
export function getPixelSceneAirMask(pixelScene, level = 0) {
	const entry = pixelSceneEntry(pixelScene, level);
	if (!entry) return null;
	// The same level getPixelSceneDrawable draws: a build has none finer than its own.
	return entry.airMasks[Math.max(entry.minLevel, level > entry.maxLevel ? entry.maxLevel : level)] ?? null;
}

function getBiomeAlias(biomeName) {
	// Aliases to avoid needing to duplicate files for repeated biomes
	if (biomeName === "coalmine_alt") return "coalmine";
	if (biomeName === "excavationsite_cube_chamber") return "excavationsite";
	if (biomeName === "snowcave_secret_chamber") return "snowcave";
	if (biomeName === "sandcave" || biomeName === "snowcastle_cavern" || biomeName === "snowcastle_hourglass_chamber") return "snowcastle";
	if (biomeName === "rainforest_open" || biomeName === "rainforest_dark") return "rainforest";
	if (biomeName === "vault_frozen") return "vault";
	if (biomeName === "the_end" || biomeName === "the_sky") return "crypt";
	if (biomeName === "scale") return "overworld";
	if (biomeName.includes("temple")) return "temple";
	if (biomeName.includes("pyramid")) return "pyramid";
	if (biomeName.includes("mountain")) return "mountain";
	return biomeName;
}

function getPixelSceneVariant(pixelSceneKey, variantKey) {
	if (variantKey === '') return PIXEL_SCENE_DATA[pixelSceneKey].imgElement;
	if (PIXEL_SCENE_DATA[pixelSceneKey].variants[variantKey]) return PIXEL_SCENE_DATA[pixelSceneKey].variants[variantKey];
	console.log(`No variant found for pixel scene key ${pixelSceneKey} with variant key ${variantKey}?`);
	return null;
}

//const GENERAL_SCENES = ["wand_altar", "wand_altar_vault", "potion_altar", "potion_altar_vault"]; // These scenes are used in multiple biomes, so we can check for them first before doing the biome-specific lookup
const GENERAL_SCENE_NAMES = GENERAL_SCENES["extras"].map(scene => scene.name);

// Don't use the alias here because spawn points can have different indices per "duplicate" biome, so we need to keep them separate in the data
function getPixelSceneKey(biomeName, sceneName) {
	if (biomeName.includes("temple")) return "temple/" + sceneName;
	if (biomeName.includes("pyramid")) return "pyramid/" + sceneName;
	if (biomeName.includes("mountain")) return "mountain/" + sceneName;
	if (!biomeName || GENERAL_SCENE_NAMES.includes(sceneName) || !GENERATOR_CONFIG[biomeName]) return "general/" + sceneName;
	return biomeName + "/" + sceneName;
}

// Every scene key the generators can place, with the (biome, scene) that first
// names it. The first biome wins because a key's spawn indices are per-biome
// and several biomes collapse onto one key (getPixelSceneKey).
function pixelSceneJobs(skip = {}) {
	const jobs = [];
	const queued = new Set();
	for (const biome of Object.keys(PIXEL_SCENE_BIOME_MAP)) {
		const biomeScenes = PIXEL_SCENE_BIOME_MAP[biome];
		for (const sceneList of Object.values(biomeScenes)) {
			for (const scene of sceneList) {
				if (scene.name === "") continue; // Skip the "no scene" option
				const key = getPixelSceneKey(biome, scene.name);
				if (skip[key] || queued.has(key)) continue;
				queued.add(key);
				// `dir` overrides the folder for the entries whose PNG does not live
				// under their own biome's name: the pyramid's interior rooms are
				// data/biome_impl/crypt/*.png (PYRAMID_SCENES).
				jobs.push({ biome, scene, key, alias: scene.dir ?? getBiomeAlias(biome) });
			}
		}
	}
	return jobs;
}

// Everything the app needs from a scene's PNGs except the pixels themselves,
// precomputed by tools/gen_pixel_scene_meta.mjs so startup decodes no images.
// A scene missing from it (the file is stale) is decoded at load, as before.
const PIXEL_SCENE_META_URL = new URL('../data/pixel_scene_meta.json', new URL("../js/pixel_scene_generation.js",import.meta.url).href);
let sceneMetaPromise = null;
function loadSceneMeta() {
	return sceneMetaPromise ??= (async () => {
		if (PIXEL_SCENE_META_URL.protocol === 'file:') {
			const fs = await import('node:fs/promises');
			return JSON.parse(await fs.readFile(PIXEL_SCENE_META_URL, 'utf8'));
		}
		const response = await fetchAsset(PIXEL_SCENE_META_URL);
		if (!response.ok) throw new Error(`HTTP ${response.status}`);
		return response.json();
	})().catch((err) => {
		console.warn('pixel_scene_meta.json unavailable; decoding every pixel scene at load:', err);
		sceneMetaPromise = null;
		return null;
	});
}

export async function loadPixelSceneData() {
	// The material atlas + band tables the textured build needs. Started here (main
	// thread, once, well before the first draw) rather than on demand, so the draw
	// path can stay synchronous and simply fall back to flat colors until it lands.
	initPixelSceneTextures();
	const jobs = pixelSceneJobs(PIXEL_SCENE_DATA);
	const meta = (await loadSceneMeta())?.scenes ?? {};
	const results = new Array(jobs.length);
	const undecoded = [];
	jobs.forEach((job, i) => {
		const m = meta[job.key];
		if (m) results[i] = recordFromMeta(job, m);
		else undecoded.push(i);
	});
	if (undecoded.length && Object.keys(meta).length) {
		console.warn(`${undecoded.length} pixel scenes missing from pixel_scene_meta.json, decoding them now; rerun tools/gen_pixel_scene_meta.mjs`);
	}

	// Bounded fan-out: fetch/unzip/decode overlap across scenes, without firing
	// hundreds of requests at once.
	let next = 0;
	const worker = async () => {
		while (next < undecoded.length) {
			const i = undecoded[next++];
			try {
				results[i] = await loadPixelSceneRecord(jobs[i]);
			} catch (err) {
				console.error(`pixel scene ${jobs[i].key} failed to load:`, err);
			}
		}
	};
	await Promise.all(Array.from({ length: Math.min(16, undecoded.length) }, worker));

	// Insert in the original order so PIXEL_SCENE_DATA iterates as it did before.
	let loaded = 0;
	for (const r of results) {
		if (!r) continue;
		PIXEL_SCENE_SPAWN_DATA[r.record.key] = r.spawnPoints;
		PIXEL_SCENE_DATA[r.record.key] = r.record;
		loaded++;
	}
	console.log(`Loaded ${loaded}/${jobs.length} pixel scenes (${undecoded.length} decoded).`);
}

// The contents of pixel_scene_meta.json, rebuilt from the PNGs. Spawn points
// are flat [x, y, spawnFunctionIndex, ...]; artMask is run lengths over the
// scene's pixels in row order, alternating uncovered/covered, starting uncovered.
export async function buildPixelSceneMeta() {
	const scenes = {};
	for (const job of pixelSceneJobs()) {
		const { meta } = await decodeSceneForMeta(job);
		scenes[job.key] = meta;
	}
	return { version: 1, scenes };
}

function sceneUrl(dir, name) {
	return `../data/pixel_scenes/${dir}/${name}.png`;
}

async function decodeSceneForMeta({ biome, scene, alias }) {
	const imgData = await loadPNG(sceneUrl(alias, scene.name), { bitmap: false });
	makeBlackTransparent(imgData.data);
	// The cell-color override art, kept as-is (its black pixels are real art, so
	// no makeBlackTransparent here). A failed fetch just means this scene renders
	// material-derived, like any other.
	let visualArt = null;
	let artMaskRuns = null;
	const artName = sceneColorsFileName(alias, scene.name);
	if (artName) {
		try {
			const vis = await loadPNG(sceneUrl(alias, artName), { bitmap: false });
			visualArt = { data: vis.data, width: vis.width, height: vis.height };
			artMaskRuns = artCoverageRuns(imgData.width, imgData.height, visualArt);
		} catch (err) {
			console.warn(`visual art ${artName}.png missing for ${alias}/${scene.name}:`, err);
		}
	}
	// Clears the spawn pixels in place when clearSpawnPixels is on; the classes
	// below never involve a spawn color, so they do not depend on that setting.
	const spawnPoints = prescanPixelScene(imgData, biome);
	const meta = {
		width: imgData.width,
		height: imgData.height,
		...classifyPixelSceneColors(imgData.data),
		spawns: spawnPoints.flatMap(p => [p.x, p.y, p.spawnFunctionIndex]),
	};
	if (artMaskRuns) meta.artMask = artMaskRuns;
	return { meta, pixels: imgData.data, visualArt };
}

// Where the art covers the scene (alpha >= 128), for the hover readout's "Art:"
// line (app.js pushOriginLines) and the edge-decal gate. It plays no part in
// which materials the edge pass sees: the art overrides cell colors only.
function artCoverageRuns(width, height, vis) {
	const runs = [];
	let covered = false, run = 0;
	for (let y = 0; y < height; y++) {
		for (let x = 0; x < width; x++) {
			const c = x < vis.width && y < vis.height && vis.data[(y * vis.width + x) * 4 + 3] >= 128;
			if (c !== covered) { runs.push(run); run = 0; covered = c; }
			run++;
		}
	}
	runs.push(run);
	return runs;
}

// Bit-packed MSB-first, one bit per scene pixel.
function artMaskFromRuns(runs, width, height) {
	const mask = new Uint8Array((width * height + 7) >> 3);
	let p = 0, covered = false;
	for (const n of runs) {
		const end = p + n;
		if (covered) for (; p < end; p++) mask[p >> 3] |= 0x80 >> (p & 7);
		p = end;
		covered = !covered;
	}
	return mask;
}

function recordFromMeta(job, m) {
	const { biome } = job;
	const spawnPoints = [];
	for (let i = 0; i < m.spawns.length; i += 3) {
		spawnPoints.push({ sourceBiome: biome, x: m.spawns[i], y: m.spawns[i + 1], spawnFunctionIndex: m.spawns[i + 2] });
	}
	const artMask = m.artMask ? artMaskFromRuns(m.artMask, m.width, m.height) : null;
	return { record: pixelSceneRecord(job, m, spawnPoints, artMask), spawnPoints };
}

function pixelSceneRecord({ biome, scene, key, alias }, m, spawnPoints, artMask) {
	return {
		key: key,
		biome: biome,
		name: scene.name,
		dir: alias, // data/pixel_scenes/<dir>/<name>.png -- the key can differ (getPixelSceneKey)
		loadGen: pixelSceneLoadGen, // which reloadPixelSceneCache() built this (injectPixelSceneData)
		// The base RGBA pixels, decoded on first use (ensureScenePixels): only the
		// overlay worker and the hover readout ever read them.
		imgElement: null,
		width: m.width,
		height: m.height,
		isCosmetic: spawnPoints.length === 0, // If there are no spawn points, we can consider it purely cosmetic and can optionally skip some checks during generation
		// Which recolor classes this scene actually contains, so it only
		// pays for a per-chunk variant when one of them depends on the
		// chunk it lands in (underlyingBiomeSuffix).
		hasAir: m.hasAir,
		hasBiomeFill: m.hasBiomeFill,
		// The game's `skip_edge_textures`: this scene's cells still erase
		// the terrain decal pass under them, but the scene runs no decal
		// pass of its own (js/pixel_scene_edge_flags.js).
		skipEdgeTextures: SKIP_EDGE_TEXTURE_SCENES.has(`${alias}/${scene.name}`),
		visualArt: null, // per-pixel cell-color override art, decoded on first use (ensureScenePixels)
		// Basename of the colors file that art came from, for the hover
		// readout -- not always `${name}_visual` (SCENE_COLORS_FILE_ALIASES).
		// Null when the scene has no art, or its file failed to load.
		artName: artMask ? sceneColorsFileName(alias, scene.name) : null,
		artMask, // bit-packed art coverage (MSB-first), or null
		// Repo-relative path of this scene's background sprite, or null.
		// Just a string: the bitmap itself is owned by the background
		// layer (js/biome_backgrounds.js), which draws it, so nothing
		// large is cloned into the overlay worker with this record.
		backgroundArt: SCENE_BACKGROUNDS[`${alias}/${scene.name}`] ?? null,
		variants: {}, // Used for color material changes, keyed as `${color}=${material}`
	};
}

// Decodes a scene's base pixels (and, with `art`, its colors-file art) onto its
// record, once per record. Records are structured-cloned between threads, so the
// in-flight loads are tracked here rather than on the record itself.
const scenePixelLoads = new WeakMap();
const sceneArtLoads = new WeakMap();

export function ensureScenePixels(data, { art = true } = {}) {
	if (!data) return Promise.resolve();
	const loads = [];
	if (!ArrayBuffer.isView(data.imgElement)) loads.push(onceFor(scenePixelLoads, data, decodeScenePixels));
	if (art && data.artName && !data.visualArt) loads.push(onceFor(sceneArtLoads, data, decodeSceneArt));
	return Promise.all(loads);
}

// For synchronous readers (the hover readout): start decoding a scene's base
// pixels and call the registered listener when they land, so the reader can
// ask again.
let scenePixelsListener = null;
export function setScenePixelsListener(fn) {
	scenePixelsListener = fn;
}
export function requestScenePixels(data) {
	ensureScenePixels(data, { art: false })
		.then(() => scenePixelsListener?.(data))
		.catch(err => console.error(`pixel scene ${data?.key} failed to decode:`, err));
}

function onceFor(loads, data, load) {
	let p = loads.get(data);
	if (!p) {
		p = load(data).catch((err) => {
			loads.delete(data);   // let a later request retry
			throw err;
		});
		loads.set(data, p);
	}
	return p;
}

async function decodeScenePixels(data) {
	const img = await loadPNG(sceneUrl(data.dir, data.name), { bitmap: false });
	if (img.width !== data.width || img.height !== data.height) {
		console.warn(`pixel scene ${data.key} is ${img.width}x${img.height} but pixel_scene_meta.json says ${data.width}x${data.height}; rerun tools/gen_pixel_scene_meta.mjs`);
	}
	makeBlackTransparent(img.data);
	if (appSettings.clearSpawnPixels) prescanPixelScene(img, data.biome);
	data.imgElement = img.data;
}

async function decodeSceneArt(data) {
	try {
		const vis = await loadPNG(sceneUrl(data.dir, data.artName), { bitmap: false });
		data.visualArt = { data: vis.data, width: vis.width, height: vis.height };
	} catch (err) {
		console.warn(`visual art ${data.artName}.png missing for ${data.dir}/${data.name}:`, err);
	}
}

// A scene pixel_scene_meta.json does not know: decode it now, and keep the
// pixels since they are already in hand.
async function loadPixelSceneRecord(job) {
	const { meta, pixels, visualArt } = await decodeSceneForMeta(job);
	const { record, spawnPoints } = recordFromMeta(job, meta);
	record.imgElement = pixels;
	record.visualArt = visualArt;
	return { record, spawnPoints };
}

// This function scans the pixel scene image for blocked room colors, and returns an array of room objects with their coordinates and colors
export function blockOutRooms(pixels, width, height) {
	let rooms = [];
	for (let y = 4; y < height; y++) {
		for (let x = 0; x < width; x++) {
			const idx = (y * width + x) * 3;
			const color = (pixels[idx] << 16) | (pixels[idx+1] << 8) | pixels[idx+2];
			if (color === 0x000000 || color === 0xffffff) continue;
			if (!BLOCKED_COLORS.includes(color)) continue;

			let startX = x+1;
			let startY = y+1;
			let endX = x+1;
			let endY = y+1;
			let foundEnd = false;
			while (!foundEnd && endX < width) {
				if (endX >= width) break;
				const tempIdx = (startY * width + endX) * 3;
				const tempColor = (pixels[tempIdx] << 16) | (pixels[tempIdx+1] << 8) | pixels[tempIdx+2];
				if (tempColor === 0x000000 || tempColor === 0x323232) {
					endX++;
					continue;
				}
				endX--;
				foundEnd = true;
			}
			if (endX >= width) endX = width - 1;
			foundEnd = false;
			while (!foundEnd && endY < height) {
				if (endY >= height) break;
				const tempIdx = (endY * width + startX) * 3;
				const tempColor = (pixels[tempIdx] << 16) | (pixels[tempIdx+1] << 8) | pixels[tempIdx+2];
				if (tempColor === 0x000000 || tempColor === 0x323232) {
					endY++;
					continue;
				}
				endY--;
				foundEnd = true;
			}
			if (endY >= height) endY = height - 1;

			if (endX > startX && endY > startY) {
				//console.log(`Blocking out room from (${startX}, ${startY}) to (${endX}, ${endY})`);

				// Block out the room with pixels to block pathfinding
				for (let by = startY; by <= endY; by++) {
					for (let bx = startX; bx <= endX; bx++) {
						const bIdx = (by * width + bx) * 3;
						// Magenta for debug
						pixels[bIdx] = 0xff;
						pixels[bIdx + 1] = 0x01;
						pixels[bIdx + 2] = 0xff;
					}
				}
			}

			rooms.push({color, startX, startY, endX, endY});
		}
	}
	return rooms;
}

const CHECK_PIXEL_SCENE_BIOME = true;

// Trailer altar example for validating that the bounds check is working correctly:
// Seed: 119164939, NG+1
// Appears in PW 0, disappears in PW -1, second one appears in PW 9

// TODO: Refactoring pixel scenes to not do the image manipulation here at all
// Instead do it in the overlay worker when generating for display
// Minimize the amount of information returned to keep the worker messages lightweight

export function loadPixelScene(biomeData, biomeName, sceneName, ws, ng, x, y, skipCosmeticScenes = true, checkBounds = true, gameMode = 'normal') {
	// The biome this scene is being stamped FOR, kept before the recolor logic
	// below rewrites biomeName to "general". Several biomes stamp one scene PNG
	// with different background art (SCENE_BACKGROUNDS_BY_BIOME), and the scene
	// key alone cannot tell them apart -- nine biomes share "general/essenceroom".
	const spawnBiome = biomeName;
	const pixelSceneKey = getPixelSceneKey(biomeName, sceneName);
	if (!PIXEL_SCENE_DATA[pixelSceneKey]) {
		console.warn(`Pixel scene data not found for key ${pixelSceneKey}. This should not happen because we preload all pixel scene images.`);
		return null;
	}
	const pixelSceneData = PIXEL_SCENE_DATA[pixelSceneKey];
	if (pixelSceneData.isCosmetic && skipCosmeticScenes) {
		// If the scene is purely cosmetic and we're skipping cosmetic scenes, skip it
		return null;
	}
	// checkBounds matches the skip_biome_checks parameter and only checks the top-left corner, while randomly placed pixel scenes check all corners
	if (checkBounds && biomeName && CHECK_PIXEL_SCENE_BIOME) {
		const topLeft = getBiomeAtWorldCoordinates(biomeData, x, y, ng > 0, gameMode);
		// Check wobbled biome based on edge noise
		if (!topLeft.biome && biomeEdgeNoiseFlag(topLeft.colorInt, 'noise_biome_edges') === null) {
			// This does not appear to ever trigger?
			console.log(`Rejected spawn for pixel scene ${sceneName} at (${x}, ${y}) with unknown biome color ${topLeft.colorInt.toString(16)} in original biome ${biomeName}. This likely means the biome map is missing colors from the data.wak unpack, such as NG+ palette swaps. Accepting spawn but returning null biome so it can be recolored without a biome-specific variant.`);
			return null;
		}
		// Unsure about this, but it would make sense... Seems to help
		if (topLeft.biome !== biomeName) {
			//console.log(`Rejected spawn for pixel scene ${sceneName} at (${x}, ${y}) in biome ${biomeName} because top-left corner is in biome ${topLeft.biome}.`);
			return null;
		}
	}
	// Recolor the pixel scene for the biome if needed
	if (!biomeName || biomeName === "general") {
		// Better fallback
		if (SCENES_TO_NOT_RECOLOR.includes(sceneName)) {
			biomeName = "general";
		} else {
			biomeName = getBiomeAtWorldCoordinates(biomeData, x + pixelSceneData.width/2, y + pixelSceneData.height/2, ng > 0, gameMode, true)?.biome || "general";
		}
	}
	// Exception to the exception because it's in two different biomes which need to be recolored differently
	// There are a few other pixel scenes which could follow this pattern, but they're drawn in the custom art already
	if (sceneName !== "the_end_shop" && GENERAL_SCENE_NAMES.includes(sceneName)) {
		biomeName = "general";
	}
	// Alternative?
	/*
	if (SCENES_TO_NOT_RECOLOR.includes(sceneName)) {
		biomeName = "general";
	}
	else if (!biomeName || biomeName === "general") {
		biomeName = getBiomeAtWorldCoordinates(biomeData, x + pixelSceneData.width/2, y + pixelSceneData.height/2, ng > 0, gameMode)?.biome || "general";
	}
	*/
	const variantKey = `biome=${biomeName}${underlyingBiomeSuffix(biomeData, pixelSceneData, biomeName, sceneName, x, y, ng, gameMode)}`;
	/*
	if (!PIXEL_SCENE_DATA[pixelSceneKey].variants[variantKey]) {
		PIXEL_SCENE_DATA[pixelSceneKey].variants[variantKey] = recolorPixelSceneForBiome(sceneName, getPixelSceneVariant(pixelSceneKey, ''), PIXEL_SCENE_DATA[pixelSceneKey].width, PIXEL_SCENE_DATA[pixelSceneKey].height, biomeName, x, y);
		//console.log(`Created biome variant of pixel scene ${pixelSceneKey} with key ${variantKey}`);
	}
	*/
	//const pixelSceneImage = PIXEL_SCENE_DATA[pixelSceneKey].variants[variantKey];
	//console.log(`Loaded pixel scene ${sceneName} for biome ${biomeName} at (${x}, ${y}) with keys ${pixelSceneKey} / ${variantKey}`);
	return {
		key: pixelSceneKey,
		variantKey: variantKey,
		name: sceneName,
		// This biome's own background art when it differs from the scene's
		// default; null means "whatever PIXEL_SCENE_DATA carries for the key".
		backgroundArt: SCENE_BACKGROUNDS_BY_BIOME[spawnBiome]?.[sceneName] ?? null,
		//imgElement: pixelSceneImage,
		width: pixelSceneData.width,
		height: pixelSceneData.height,
		x: x,
		y: y,
		//spawnPoints: pixelSceneData.spawnPoints,
		material: null,
		type: 'pixel_scene'
	};
}

export function loadRandomPixelScene(biomeData, biomeName, scene_list, ws, ng, x, y, skipCosmeticScenes = true, gameMode = 'normal') {
	if (!scene_list || scene_list.length === 0) return null;
	const prng = new NollaPrng(0);
	// A unique scene (the Collapsed Mines shrine) is placed once per run: after
	// the player has loaded it, it is out of the pool. The setting is read here
	// rather than patched into the scene table, so every thread that scans --
	// the page and the world workers, which get the settings but have their own
	// copy of the table -- answers the same.
	const probOf = (scene) => (scene.unique && appSettings.visitedCoalmineAltShrine ? 0 : scene.prob);
	let total_prob = 0;
	for (const scene of scene_list) {
		total_prob += probOf(scene);
	}
	let r = prng.ProceduralRandom(ws + ng, x, y) * total_prob;
	for (const scene of scene_list) {
		const prob = probOf(scene);
		if (prob <= 0) continue;
		if (r <= prob) {
			if (scene.name === "") return null; // Rolled for no scene
			const pixelSceneKey = getPixelSceneKey(biomeName, scene.name);
			if (!PIXEL_SCENE_DATA[pixelSceneKey]) {
				console.warn(`Pixel scene data not found for key ${pixelSceneKey}. This should not happen because we preload all pixel scene images.`);
				return null;
			}
			const pixelSceneData = PIXEL_SCENE_DATA[pixelSceneKey];
			if (pixelSceneData.isCosmetic && skipCosmeticScenes) {
				// If the scene is purely cosmetic and we're skipping cosmetic scenes, skip it
				return null;
			}
			let outputScene = {
				key: pixelSceneKey,
				// This biome's own background art when it differs from the scene's
				// default; null means "whatever PIXEL_SCENE_DATA carries for the key".
				backgroundArt: SCENE_BACKGROUNDS_BY_BIOME[biomeName]?.[scene.name] ?? null,
				name: scene.name, 
				//imgElement: pixelSceneData.imgElement,
				width: pixelSceneData.width,
				height: pixelSceneData.height,
				x: x, 
				y: y, 
				material: null,
				//spawnPoints: pixelSceneData.spawnPoints,
				type: 'pixel_scene'
			};
			// Check all four corners to make sure they are in the same biome
			if (CHECK_PIXEL_SCENE_BIOME) {
				const w = pixelSceneData.width;
				const h = pixelSceneData.height;
				const corners = [
					[x, y],
					[x + w, y],
					[x, y + h],
					[x + w, y + h],
				];
				for (const [cx, cy] of corners) {
					const res = getBiomeAtWorldCoordinates(biomeData, cx, cy, ng > 0, gameMode, true);
					// Reject if the corner's wobbled biome is different (including null)
					if (res.biome !== biomeName) {
						return null;
					}
				}
			}
			// Recolor random materials first so material lookups still work
			let variantKey = '';
			if (scene.color_material) {
				// Sort keys to ensure consistent ordering for caching variants
				const sortedColors = Object.keys(scene.color_material).sort((a, b) => parseInt(a, 16) - parseInt(b, 16));
				for (const color of sortedColors) {
					// Start with the recolored variant
					//let pixelSceneImage = getPixelSceneVariant(pixelSceneKey, variantKey);
					const materials = scene.color_material[color];
					prng.SetRandomSeed(ws + ng, x + 11, y - 21); //?
					let r = prng.ProceduralRandom(ws + ng, x + 11, y - 21); // Note ProceduralRandom returns a value in (0, 1] so this is actually fine
					let mr = Math.ceil(r * materials.length) - 1;
					
					const targetMaterial = materials[mr];
					outputScene.material = targetMaterial;
					let materialColor = MATERIAL_WANG_COLORS[materials[mr]];
					// See if the recolored pixel scene is already cached
					//variantKey += (variantKey !== '' ? '&' : '') + `${color}=${targetMaterial}`;
					// Use material color instead to avoid an extra lookup
					variantKey += (variantKey !== '' ? '&' : '') + `${color}=${materialColor}`;
					/*
					if (!PIXEL_SCENE_DATA[pixelSceneKey].variants[variantKey]) {
						PIXEL_SCENE_DATA[pixelSceneKey].variants[variantKey] = recolorPixelScene(
							pixelSceneImage, 
							parseInt(color, 16), 
							parseInt(materialColor, 16)
						);
						//console.log(`Created variant of pixel scene ${pixelSceneKey} with key ${variantKey}`);
					}
					*/
				}
			}
			// Recolor the pixel scene for the biome if needed
			const finalVariantKey = variantKey + (variantKey !== '' ? '&' : '')
				+ `biome=${biomeName}${underlyingBiomeSuffix(biomeData, pixelSceneData, biomeName, scene.name, x, y, ng, gameMode)}`;
			/*
			if (!PIXEL_SCENE_DATA[pixelSceneKey].variants[finalVariantKey]) {
				PIXEL_SCENE_DATA[pixelSceneKey].variants[finalVariantKey] = recolorPixelSceneForBiome(scene.name, getPixelSceneVariant(pixelSceneKey, variantKey), biomeName);
				//console.log(`Created biome variant of pixel scene ${pixelSceneKey} with key ${finalVariantKey}`);
			}
			*/
			outputScene.variantKey = finalVariantKey;
			//outputScene.imgElement = PIXEL_SCENE_DATA[pixelSceneKey].variants[finalVariantKey];

			//console.log(`Loaded pixel scene ${scene.name} at (${x}, ${y}) in biome ${biomeName}.`);
			return outputScene;
		}
		r -= prob;
	}
	console.log(`Impossible zero probability outcome for pixel scene at (${x}, ${y}), total prob ${total_prob}, r ${r}: ${JSON.stringify(scene_list)}`);
	return null;
}

/** Does this scene contain air pixels / "fill with the biome's material" pixels? */
function classifyPixelSceneColors(data) {
	let hasAir = false;
	let hasBiomeFill = false;
	for (let i = 0; i < data.length; i += 4) {
		const r = data[i], g = data[i + 1], b = data[i + 2];
		if (r === 0 && g === 0 && b === 0x42) hasAir = true;
		else if (r === g && g === b && r > 0) hasBiomeFill = true;
		if (hasAir && hasBiomeFill) break;
	}
	return { hasAir, hasBiomeFill };
}

/**
 * The `@<biome>` suffix a scene's `biome=` variant key carries when its recolor
 * depends on the chunk it landed in, or '' when it does not.
 *
 * The recolor target is the scene's *folder* biome, which for everything under
 * general/ and temple/ is a pseudo-biome shared across the map. Two things it
 * therefore cannot answer: whether the chunk beneath is solid material, which
 * decides if the scene's air may stay transparent; and what color the scene's
 * "fill with the biome's own material" pixels take, since a pseudo-biome has no
 * entry in either color table -- which is what used to paint the orb room floors
 * magenta. Naming the chunk's biome in the variant key answers both, at one
 * cached variant per underlying biome, and only for scenes that contain the
 * class in question.
 */
function underlyingBiomeSuffix(biomeData, sceneData, biomeName, sceneName, x, y, ng, gameMode) {
	// The altars are deliberately never recolored per biome; keep them at one variant.
	if (SCENES_TO_NOT_RECOLOR.includes(sceneName)) return '';
	const needsFillColor = sceneData.hasBiomeFill && TILE_OVERLAY_COLORS[biomeName] === undefined;
	if (!sceneData.hasAir && !needsFillColor) return '';
	const under = getBiomeAtWorldCoordinates(biomeData, x + sceneData.width / 2, y + sceneData.height / 2, ng > 0, gameMode, true)?.biome;
	if (!under || under === biomeName) return '';
	if (needsFillColor) return `@${under}`;
	return terrainFillColorForBiome(under) === undefined ? '' : `@${under}`;
}

/**
 * How a scene stamped in `targetBiome` paints its two biome-dependent color
 * classes: the density ("fill with this biome's own material") class, and air.
 *
 * Shared by the flat recolor and the textured build so the two cannot drift on
 * which biome answers for a pseudo-biome folder, or on when air is opaque.
 */
function sceneBiomePaint(sceneName, targetBiome) {
	// `biome=<folder>@<chunk biome>`: the folder decides the colors, the suffix
	// (underlyingBiomeSuffix) names the biome of the chunk the scene landed in.
	const at = targetBiome.indexOf('@');
	const underlyingBiome = at < 0 ? null : targetBiome.slice(at + 1);
	if (at >= 0) targetBiome = targetBiome.slice(0, at);
	// "the chunk under this scene is solid material", NOT merely "the biome has a
	// fillMaterial": a `sceneOnly` room has one (its scenes' white resolves through
	// it) but its chunk is air, so the scene's air must stay transparent there.
	const fillBiomeUnderScene = (underlyingBiome && terrainFillColorForBiome(underlyingBiome) !== undefined)
		? underlyingBiome : null;

	// A scene's gray/white pixels are the engine's "fill with this biome's own
	// material" class, so in a constant-material biome they must come out as that
	// biome's fill color -- the same one the terrain around them paints. Otherwise
	// they take the hand-authored foreground color, which for these biomes equals
	// the background color and leaves the carved room reading as a flat block.
	// The pseudo-biomes (general/, temple/, spliced/) have no entry in either
	// color table, so their scenes' fill pixels used to come out magenta -- most
	// visibly the orb rooms, whose whole floor is that class. Falling back to the
	// biome under the scene answers it exactly: those pixels are "fill with the
	// chunk's own material", which is what the suffix names.
	const targetColor = sceneMaterialFillColorForBiome(targetBiome)
		?? TILE_OVERLAY_COLORS[targetBiome]
		?? (underlyingBiome ? sceneMaterialFillColorForBiome(underlyingBiome) ?? TILE_OVERLAY_COLORS[underlyingBiome] : undefined)
		?? 0xff00ff;
	let bgColor = BIOME_BACKGROUND_COLORS[targetBiome]
		?? (underlyingBiome ? BIOME_BACKGROUND_COLORS[underlyingBiome] : undefined)
		?? 0x000000;

	// Air over a fill has to punch a visible hole in solid material, so it must
	// not come out the same color as the fill. For friend_1..6 the biome maps
	// give foreground and background the same unauthored pixel, so the "correct"
	// background would erase the room; darken the fill instead, which is roughly
	// what a background reads as next to its own material anyway.
	const fillUnder = terrainFillColorForBiome(fillBiomeUnderScene ?? targetBiome);
	if (fillUnder !== undefined && channelDistance(bgColor, fillUnder) <= AIR_OVER_FILL_TOLERANCE) {
		bgColor = ((Math.round(((fillUnder >> 16) & 0xFF) * AIR_OVER_FILL_DARKEN) << 16)
			| (Math.round(((fillUnder >> 8) & 0xFF) * AIR_OVER_FILL_DARKEN) << 8)
			| Math.round((fillUnder & 0xFF) * AIR_OVER_FILL_DARKEN));
	}

	// Transparent air is only right when nothing is painted underneath: it lets
	// whatever is already on the canvas show through, which for a wang biome is
	// the layer's own terrain. In a constant-material fill biome the whole chunk
	// is solid, so transparent air would leave the carved room filled in and
	// invisible -- the scene has to punch the hole itself, in the color the
	// background layer would have shown. Hiisi base is the hand-found instance of
	// the same rule.
	//
	// Both tests ask "does this chunk actually emit a fill layer", so a
	// `sceneOnly` room falls through to the transparent branch: its chunk is air,
	// and the scene's air must read as the cave background the game shows there,
	// not as an opaque plug.
	//
	// The textured build does not need any of this -- it erases the terrain under
	// its air with a real mask pass -- so it only honors the authored exceptions.
	const opaqueOverFill = targetBiome === "snowcastle" || !!fillBiomeUnderScene
		|| terrainFillColorForBiome(targetBiome) !== undefined;
	const authoredAirAlpha = PIXEL_SCENE_AIR_TRANSPARENCY_EXCEPTIONS[sceneName] ?? 0x00;

	return {
		targetBiome, underlyingBiome, targetColor, bgColor,
		airAlpha: opaqueOverFill ? 0xff : authoredAirAlpha,
		texturedAirAlpha: authoredAirAlpha,
	};
}

export function recolorPixelSceneForBiome(sceneName, sourceData, targetBiome) {
	//const recolorMaterials = document.getElementById('recolor-materials').checked;
	const recolorMaterials = appSettings.recolorMaterials;

	const outData = new Uint8Array(sourceData.length);
	outData.set(sourceData);

	// Some scene name exceptions because this just isn't working
	const paint = sceneBiomePaint(sceneName, targetBiome);
	const targetColor = paint.targetColor;
	const bgColor = paint.bgColor;
	const airAlpha = paint.airAlpha;
	let targetR = (targetColor >> 16) & 0xFF;
	let targetG = (targetColor >> 8) & 0xFF;
	let targetB = targetColor & 0xFF;
	let bgColorR = (bgColor >> 16) & 0xFF;
	let bgColorG = (bgColor >> 8) & 0xFF;
	let bgColorB = bgColor & 0xFF;
	/*
	const biomeMapWidth = getWorldSize(app.ngPlusCount > 0);
	if (targetR === 255 && targetG === 0 && targetB === 255) {
		// As a fallback, use the color of the biome map?
		// TODO: Currently using app references here when I probably shouldn't
		// Using center of the pixel scene, but it shouldn't really matter
		const chunkX = (Math.floor((biomeMapWidth * 256 + x + width/2) / 512) % getWorldSize(app.ngPlusCount > 0) + getWorldSize(app.ngPlusCount > 0)) % getWorldSize(app.ngPlusCount > 0);
		let chunkY = Math.floor((14*512 + y + height/2) / 512);
		if (chunkY < 0) chunkY = 0;
		if (chunkY > 47) chunkY = 47;
		const bgColorIdx = (chunkY * biomeMapWidth + chunkX)*3;
		const bgColor = (app.recolorOffscreenBuffer[bgColorIdx] << 16) | (app.recolorOffscreenBuffer[bgColorIdx + 1] << 8) | app.recolorOffscreenBuffer[bgColorIdx + 2];
		if (y > 22000) {
			console.log(bgColor, BIOME_BACKGROUND_COLORS["the_end"] & 0xffffff)
		}
		// TODO: Some attempt to make the background color not exactly the same as the terrain color... Need to just get a better background and foreground.
		targetR = Math.floor(app.recolorOffscreenBuffer[bgColorIdx] * 0.75);
		targetG = Math.floor(app.recolorOffscreenBuffer[bgColorIdx + 1] * 0.75);
		targetB = Math.floor(app.recolorOffscreenBuffer[bgColorIdx + 2] * 0.75);
		bgColorR = 0;
		bgColorG = 0;
		bgColorB = 0;
	}
	*/

	for (let i = 0; i < outData.length; i += 4) {
		const r = outData[i];
		const g = outData[i + 1];
		const b = outData[i + 2];

		// Handle Grays (Material Recolor)
		if (r === g && g === b && r > 0) {
			outData[i] = targetR;
			outData[i + 1] = targetG;
			outData[i + 2] = targetB;
			//outData[i + 3] = targetA;
		} 
		// Replace Air with Background
		else if (r === 0x00 && g === 0x00 && b === 0x42) {
			outData[i] = bgColorR;
			outData[i + 1] = bgColorG;
			outData[i + 2] = bgColorB;
			outData[i + 3] = airAlpha;
		}
		// Recolor Wang Colors
		else if (recolorMaterials && (r > 0 || g > 0 || b > 0)) {
			const rgb = (r << 16) | (g << 8) | b;
			const matColor = MATERIAL_COLOR_CONVERSION[rgb];
			if (matColor) {
				outData[i] = (matColor >> 16) & 0xFF;
				outData[i + 1] = (matColor >> 8) & 0xFF;
				outData[i + 2] = matColor & 0xFF;
			}
		}
	}

	return outData;
}

// Width and height are not actually needed here
export function recolorPixelScene(sourceData, sourceColor, targetColor) {
    const outData = new Uint8Array(sourceData.length);
	outData.set(sourceData);

	const sourceR = (sourceColor >> 16) & 0xFF;
	const sourceG = (sourceColor >> 8) & 0xFF;
	const sourceB = sourceColor & 0xFF;

	const targetR = (targetColor >> 16) & 0xFF;
	const targetG = (targetColor >> 8) & 0xFF;
	const targetB = targetColor & 0xFF;

    for (let i = 0; i < outData.length; i += 4) {
        const r = outData[i];
		const g = outData[i + 1];
		const b = outData[i + 2];
		if (r === sourceR && g === sourceG && b === sourceB) {
			outData[i] = targetR;
			outData[i + 1] = targetG;
			outData[i + 2] = targetB;
		}
    }

    return outData;
}

// 0xRRGGBB wang color -> compositing alpha, for materials drawn in a flat color
// (no texture): the XML color's alpha byte, as texturePixelSceneForBiome uses.
// Textured materials come from the caller (texturedFlatAlphas).
let flatWangAlpha = null;
function flatMaterialAlpha(rgb) {
	if (!flatWangAlpha) {
		flatWangAlpha = new Map();
		for (const m of MATERIAL_DATA) {
			if (m.texture || !m.wang || !m.color) continue;
			const a = (parseInt(m.color, 16) >>> 24) & 0xff;
			if (a < 255) flatWangAlpha.set(parseInt(m.wang, 16) & 0xffffff, a);
		}
	}
	return flatWangAlpha.get(rgb) ?? 255;
}

/**
 * The erase-aware variant of a flat (zoomed-out) scene build, for when the
 * terrain under it is the GL terrain that level 0's textured build erases.
 * Without it, zoomed out, a scene's air let the terrain fill its rooms and its
 * liquids (acid, water, blood) painted as opaque neon slabs.
 *
 * `wangPixels` is the scene after its material substitutions (still wang
 * colors); `flatPixels` is recolorPixelSceneForBiome's output for it. Mirrors
 * texturePixelSceneForBiome's air and translucency rules: FORCE AIR goes in the
 * mask unless the scene paints its air solid, and a translucent material keeps
 * its alpha and erases the terrain under it. `texturedAlphas` is
 * texturedFlatAlphas(): a textured material's alpha is its texels' mean. The density class stays flat -- its
 * per-cell band choice (which can answer air) is position-dependent, and this
 * build is shared by every instance.
 */
export function eraseFlatScenePixels(sceneName, wangPixels, flatPixels, targetBiome, texturedAlphas = []) {
	const airAlpha = sceneBiomePaint(sceneName, targetBiome).texturedAirAlpha;
	const texAlpha = new Map(texturedAlphas);
	const out = new Uint8Array(flatPixels);
	let airMask = null;
	for (let i = 0; i < wangPixels.length; i += 4) {
		if (wangPixels[i + 3] === 0) continue;
		const r = wangPixels[i], g = wangPixels[i + 1], b = wangPixels[i + 2];
		let a, erase;
		if (r === 0x00 && g === 0x00 && b === 0x42) {
			// A scene that paints its air solid leaves the terrain under it alone.
			a = airAlpha;
			erase = airAlpha === 0x00;
		} else {
			if (r === g && g === b) continue;
			const rgb = (r << 16) | (g << 8) | b;
			a = texAlpha.get(rgb) ?? flatMaterialAlpha(rgb);
			erase = a < 255;
		}
		out[i + 3] = a;
		if (erase) {
			if (!airMask) airMask = new Uint8Array(wangPixels.length);
			airMask[i + 3] = 0xff;
		}
	}
	return { pixels: out, airMask };
}
// ---------------------------------------------------------------------------
// Engine-faithful pixel scene texturing
//
// The game does not paint a stamped scene in flat material colors. Every cell it
// places gets its color from the material's materials_gfx texture, sampled at the
// cell's ABSOLUTE world coordinates with a negative-safe modulo
// (CellFactory_GetCellColor @0x007044a0) -- exactly the rule the GL terrain
// shader already reproduces for terrain. Two consequences for this module:
//
//  * the answer depends on where the scene landed, so a recolored variant can no
//    longer be shared by every instance of the same scene. The variant cache below
//    keeps doing what it is good at (material SUBSTITUTION, which is
//    position-independent), and the texturing happens per instance when its
//    bitmap is built.
//  * a scene's white/gray pixels are density 1.0 fed to the biome's
//    <MaterialComponent> bands, not a single flat color -- and the rare gates in
//    those bands are position-dependent too. band_select.js runs the engine's own
//    chooser for them.
//
// Both modules are pulled in dynamically so the overlay/search workers, which
// import this file for the flat recolor and the material lookups, never load the
// 270 KB engine tables or the material atlas.
// ---------------------------------------------------------------------------

let sceneTextureModules = null;   // { atlas: <material_atlas.js>, bands: <band_select.js> }
let sceneTextureLoading = null;

/** Starts (or joins) the one-time load of the atlas + band tables. Main thread. */
export function initPixelSceneTextures() {
	return sceneTextureLoading ??= (async () => {
		const [atlas, bands] = await Promise.all([
			Promise.resolve(__static_import_0),
			Promise.resolve(__static_import_1),
		]);
		await atlas.initMaterialAtlas();
		sceneTextureModules = { atlas, bands };
		return sceneTextureModules;
	})().catch((err) => {
		console.warn('[pixel scenes] material textures unavailable, staying on flat colors:', err);
		return null;
	});
}

/**
 * The loaded atlas when scene texturing is both enabled and ready, else null.
 *
 * Gated exactly as the GL terrain's own material textures are (app.js passes
 * `materialTextures && recolorMaterials` to the renderer), so the one toggle
 * moves scenes and terrain together and "material colors off" still means flat.
 */
function sceneTextureAtlas() {
	if (!appSettings.materialTextures || !appSettings.recolorMaterials || !sceneTextureModules) return null;
	return sceneTextureModules.atlas.getMaterialAtlas();
}

// 0xRRGGBB wang color -> material name, from MATERIAL_COLOR_LOOKUP's hex keys.
let wangColorToMaterial = null;
function materialForWangColor(rgb) {
	if (!wangColorToMaterial) {
		wangColorToMaterial = new Map();
		for (const [hex, name] of Object.entries(MATERIAL_COLOR_LOOKUP)) {
			wangColorToMaterial.set(parseInt(hex, 16) & 0xffffff, name);
		}
	}
	return wangColorToMaterial.get(rgb);
}

/**
 * The BIOME_ENGINE entry whose <MaterialComponent> bands answer this scene's
 * density class, preferring the scene's own folder biome and falling back to the
 * biome of the chunk it landed in (which is what a pseudo-biome folder means).
 * Only an entry that actually carries bands counts: the constant-material rooms
 * (temple_altar and friends) have an empty table and are answered by their
 * fillMaterial instead.
 *
 * Deliberately NOT gated on `biome.supported`. That flag is about the TOPOLOGY
 * resolve -- whether the carve branch, the inside-noise variant and the density
 * modifier of this biome are ported -- and a scene's density class runs none of
 * it: the coverage is a literal 1.0 that the caller feeds straight into
 * ComputeMaterialNoiseDensity. The band list is the only thing that has to be
 * complete, and it is: of the 174 biomes in the table exactly one (lava_90percent
 * #ffa717) loses a band to `unsupportedBand`, and it loses its only one, so the
 * `bands.length` test below already excludes it.
 *
 * Requiring `supported` cost the 41 biomes that carry bands but fail the topology
 * checks -- every orb room, every essence room, the gun/null/rock/song/moon rooms,
 * secret_lab, the pyramid interiors -- which are exactly the rooms whose entire
 * content is a stamped scene. They all fell back to a single flat fillMaterial.
 * Measured against the game on the orb room (groundtruth/batch3/orbroom, seed
 * 786433191): the flat fill matched 52.3% of the scene's 48,936 white pixels,
 * the band chooser matches 99.4% -- including the 17.9% that are AIR, which is
 * the orb room's background art showing through its floor.
 */
function densityBiomeFor(bands, targetBiome, underlyingBiome) {
	for (const name of [targetBiome, underlyingBiome]) {
		if (!name) continue;
		const conf = GENERATOR_CONFIG[name];
		if (!conf) continue;
		const biome = bands.engineBiomeForColor(conf.color & 0xffffff);
		// The NAME rides along so the hover readout can say which biome's bands
		// answered, which is the whole point of the pseudo-biome fallback.
		if (biome && biome.bands.length) return { entry: biome, biomeName: name };
	}
	return null;
}

/** The fillMaterial that answers a scene's density class where no band table does. */
function densityFillMaterialFor(targetBiome, underlyingBiome) {
	for (const name of [targetBiome, underlyingBiome]) {
		const conf = name ? GENERATOR_CONFIG[name] : null;
		const material = conf ? FILL_BIOME_MATERIALS[conf.color & 0xffffff] : undefined;
		if (material !== undefined) return { material, biomeName: name };
	}
	return null;
}

/**
 * The two biome names a variant key's `biome=` part carries: the scene's folder
 * biome and, after `@`, the biome of the chunk it landed in.
 */
function variantBiomes(variantKey) {
	let biome = 'general';
	for (const part of (variantKey || '').split('&')) {
		const eq = part.indexOf('=');
		if (eq >= 0 && part.slice(0, eq) === 'biome') biome = part.slice(eq + 1);
	}
	const at = biome.indexOf('@');
	return {
		targetBiome: at < 0 ? biome : biome.slice(0, at),
		underlyingBiome: at < 0 ? null : biome.slice(at + 1),
	};
}

/**
 * What a stamped scene's DENSITY CLASS -- its white/gray pixels, the engine's
 * "fill with this biome's own material" -- resolves to at one world pixel.
 *
 * The same lookup texturePixelSceneForBiome() runs per pixel while it builds an
 * instance, pulled out so the hover readout can answer for those pixels too
 * (js/utils.js getMaterialProvenanceAtWorldCoordinates). Without it the readout
 * walked the scene PNG, found a colour no material table knows, and reported
 * `Origin: air` over solid rock -- the blind spot that made the orb rooms look
 * empty while the renderer was painting them correctly.
 *
 * Returns null when nothing can answer (no bands loaded and no fill material).
 * `{ material: null, via: 'bands' }` is NOT that case: it means the band table
 * accepted nothing at density 1.0, which is a real AIR answer.
 *
 * @param {string} variantKey  the instance's variant key (only `biome=` is read)
 * @param {number} worldX      absolute world coordinates of the pixel
 * @param {number} worldY
 * @returns {{material: string|null, biomeName: string, via: 'bands'|'fill'}|null}
 */
export function sceneDensityClassAt(variantKey, worldX, worldY) {
	const { targetBiome, underlyingBiome } = variantBiomes(variantKey);
	// Gated exactly as the build is: with material textures off the scene paints
	// the flat fill colour, so claiming a band's answer would name a material the
	// picture on screen does not show.
	const bands = sceneTextureAtlas() ? sceneTextureModules.bands : null;
	const densityBiome = bands ? densityBiomeFor(bands, targetBiome, underlyingBiome) : null;
	if (densityBiome) {
		const id = bands.selectComponentForCell(densityBiome.entry, worldX, worldY,
			bands.computeMaterialNoiseDensity(worldX, worldY, 1.0));
		return { material: id < 0 ? null : bands.materialNameForId(id), biomeName: densityBiome.biomeName, via: 'bands' };
	}
	const fill = densityFillMaterialFor(targetBiome, underlyingBiome);
	return fill ? { material: fill.material, biomeName: fill.biomeName, via: 'fill' } : null;
}

/**
 * Per-instance engine-faithful build of one stamped pixel scene.
 *
 * `sourceData` is the scene PNG after its material substitutions (so its pixels
 * are still wang colors); (worldX, worldY) is where its top-left corner lands in
 * generation world space. Returns { pixels, airMask }: the RGBA image to draw,
 * and an RGBA mask that is opaque exactly on the scene's FORCE AIR pixels, or
 * null when it has none.
 *
 * Air (#000042) erases the world instead of painting it, so the caller draws the
 * mask with `destination-out` before the image. That is what makes air correct
 * over the engine-resolved GL terrain, which paints every chunk -- the old
 * "opaque background color over a fill biome" trick only covered the chunks
 * telescope itself filled.
 */
const SCENE_CHOOSER_MAX_BLOCK_LOG2 = 2;
export function texturePixelSceneForBiome(sceneName, sourceData, width, height, targetBiome, worldX, worldY, level = 0) {
	const { atlas: atlasMod, bands } = sceneTextureModules;
	const atlas = atlasMod.getMaterialAtlas();
	const recolorMaterials = appSettings.recolorMaterials;
	const paint = sceneBiomePaint(sceneName, targetBiome);
	const airAlpha = paint.texturedAirAlpha;
	const bgColorR = (paint.bgColor >> 16) & 0xFF;
	const bgColorG = (paint.bgColor >> 8) & 0xFF;
	const bgColorB = paint.bgColor & 0xFF;

	// The density class, resolved per pixel. A scene's gray pixels are fully solid
	// coverage, but the engine never hands raw coverage to the band chooser: it
	// runs it through ComputeMaterialNoiseDensity first, which warps the position
	// by a value-noise field and adds a simplex term. That is why a uniformly
	// white region comes out of the game as veins of several materials rather than
	// one flat answer, and it is the same call the GL terrain under the scene makes.
	const densityBiome = densityBiomeFor(bands, paint.targetBiome, paint.underlyingBiome);

	// How one material paints: its atlas rect when it has a texture, else its flat
	// display color (which is what the engine falls back to as well), composited
	// with the material's XML alpha (water 0xA0) exactly as the engine's cell
	// grid blends liquids/gasses over the background layer. Memoized -- a scene
	// uses a handful of materials across hundreds of thousands of pixels.
	const recipeByName = new Map();
	const recipeForMaterial = (name, fallbackFlat) => {
		let recipe = recipeByName.get(name);
		if (recipe === undefined) {
			const wang = MATERIAL_WANG_COLORS[name];
			const entry = atlasMod.materialAtlasEntry(atlas, name);
			recipe = {
				entry,
				flat: wang === undefined ? fallbackFlat : materialDisplayColor(name),
				alpha: wang === undefined ? 255 : atlasMod.materialAlpha(name),
				// What the texels average to, for the zoomed-out build.
				meanRGB: entry > 0 ? atlasMod.atlasEntryMeanRGB(atlas, entry) : 0,
				meanAlpha: entry > 0 ? atlasMod.atlasEntryMeanAlpha(atlas, entry) : 255,
			};
			recipeByName.set(name, recipe);
		}
		return recipe;
	};
	// The density class' answer where no band table covers it: the biome's own
	// fillMaterial, textured like any other material rather than flattened.
	const fillMaterial = densityFillMaterialFor(paint.targetBiome, paint.underlyingBiome);
	const fillRecipe = fillMaterial
		? recipeForMaterial(fillMaterial.material, paint.targetColor)
		: { entry: 0, flat: paint.targetColor, alpha: 255 };

	const densityRecipes = new Map();
	const densityRecipeFor = (id) => {
		let recipe = densityRecipes.get(id);
		if (recipe === undefined) {
			const name = id < 0 ? null : bands.materialNameForId(id);
			recipe = name ? recipeForMaterial(name, paint.targetColor) : fillRecipe;
			densityRecipes.set(id, recipe);
		}
		return recipe;
	};
	// Wang color -> the same recipe, via the material that owns the color.
	const wangRecipes = new Map();
	const wangRecipeFor = (rgb) => {
		let recipe = wangRecipes.get(rgb);
		if (recipe === undefined) {
			const name = materialForWangColor(rgb);
			recipe = name
				? recipeForMaterial(name, (recolorMaterials ? MATERIAL_COLOR_CONVERSION[rgb] : undefined) ?? rgb)
				: { entry: 0, flat: (recolorMaterials ? MATERIAL_COLOR_CONVERSION[rgb] : undefined) ?? rgb, alpha: 255 };
			wangRecipes.set(rgb, recipe);
		}
		return recipe;
	};

	const outData = new Uint8Array(sourceData.length);
	outData.set(sourceData);
	let airMask = null;

	// Zoomed out (level > 0) the result is only ever seen halved `level` times,
	// so the density class asks the band chooser once per block (at its center)
	// instead of once per pixel, and textured materials paint their texels' mean
	// rather than the texel. The block stops at 4x4 (SCENE_CHOOSER_MAX_BLOCK_LOG2):
	// a rare band (ore specks) caught by one sample of a 16x16 block paints the
	// whole block, a full-bright speck the halving can no longer average down.
	const blockLog2 = Math.min(level, SCENE_CHOOSER_MAX_BLOCK_LOG2);
	const blocksW = level > 0 ? ((width + (1 << blockLog2) - 1) >> blockLog2) : 0;
	const blockChoice = level > 0 ? new Int32Array(blocksW * ((height + (1 << blockLog2) - 1) >> blockLog2)).fill(-2) : null;
	const blockHalf = level > 0 ? 1 << (blockLog2 - 1) : 0;

	for (let i = 0, p = 0; i < outData.length; i += 4, p++) {
		if (outData[i + 3] === 0) continue;   // untouched: alpha 0 / #000000
		const r = outData[i], g = outData[i + 1], b = outData[i + 2];

		// FORCE AIR: erases the world rather than painting it, so it goes in the
		// mask -- unless this scene is one of the few whose art wants its air
		// painted solid, in which case the image covers the same pixel and the mask
		// must leave it alone (that keeps the two passes disjoint, so the draw site
		// can run them in either order).
		if (r === 0x00 && g === 0x00 && b === 0x42) {
			if (airAlpha === 0x00) {
				if (!airMask) airMask = new Uint8Array(sourceData.length);
				airMask[i + 3] = 0xff;
			}
			outData[i] = bgColorR;
			outData[i + 1] = bgColorG;
			outData[i + 2] = bgColorB;
			outData[i + 3] = airAlpha;
			continue;
		}

		const wx = worldX + (p % width);
		const wy = worldY + ((p / width) | 0);

		// Full coverage through the biome's <MaterialComponent> bands for the gray
		// class; the wang color's own material for everything else.
		//
		// A band table that accepts nothing at density 1.0 answers AIR, and that is
		// a real answer rather than a gap: the orb room's floor is 17.9% air in the
		// game's own dump and the chooser predicts it (see densityBiomeFor). So a
		// -1 from a table we have goes into the erase mask exactly like FORCE AIR,
		// which is what lets a room's background art show through its own floor.
		// Only a biome with NO band table falls back to the flat fillMaterial.
		let recipe;
		if (r === g && g === b && r > 0) {
			if (densityBiome) {
				let id;
				if (blockChoice) {
					const lx = p % width, ly = (p / width) | 0;
					const b = (ly >> blockLog2) * blocksW + (lx >> blockLog2);
					id = blockChoice[b];
					if (id === -2) {
						const bx = worldX + ((lx >> blockLog2) << blockLog2) + blockHalf;
						const by = worldY + ((ly >> blockLog2) << blockLog2) + blockHalf;
						id = blockChoice[b] = bands.selectComponentForCell(densityBiome.entry, bx, by,
							bands.computeMaterialNoiseDensity(bx, by, 1.0));
					}
				} else {
					id = bands.selectComponentForCell(densityBiome.entry, wx, wy,
						bands.computeMaterialNoiseDensity(wx, wy, 1.0));
				}
				if (id < 0) {
					if (!airMask) airMask = new Uint8Array(sourceData.length);
					airMask[i + 3] = 0xff;
					outData[i + 3] = 0x00;
					continue;
				}
				recipe = densityRecipeFor(id);
			}
			else recipe = fillRecipe;
		}
		else recipe = wangRecipeFor((r << 16) | (g << 8) | b);
		const { entry, flat, alpha } = recipe;

		let a = alpha;
		if (entry > 0 && level > 0) {
			a = recipe.meanAlpha;
			outData[i] = (recipe.meanRGB >> 16) & 0xFF;
			outData[i + 1] = (recipe.meanRGB >> 8) & 0xFF;
			outData[i + 2] = recipe.meanRGB & 0xFF;
		} else if (entry > 0) {
			// Textured materials take the texel's own alpha (same rule as the GL
			// terrain shader); a fully transparent texel places no cell at all.
			const texel = atlasMod.materialTexelRGBA(atlas, entry, wx, wy);
			if (texel < 0) { outData[i + 3] = 0x00; continue; }
			a = (texel >>> 24) & 0xFF;
			outData[i] = (texel >> 16) & 0xFF;
			outData[i + 1] = (texel >> 8) & 0xFF;
			outData[i + 2] = texel & 0xFF;
		} else {
			outData[i] = (flat >> 16) & 0xFF;
			outData[i + 1] = (flat >> 8) & 0xFF;
			outData[i + 2] = flat & 0xFF;
		}
		if (a < 255) {
			// A translucent cell (liquid, gas) replaces the world cell under it
			// and blends over the BACKGROUND, so it must also erase the terrain:
			// mask first, translucent pixel second, background refilled behind by
			// the draw site's destination-over pass.
			outData[i + 3] = a;
			if (!airMask) airMask = new Uint8Array(sourceData.length);
			airMask[i + 3] = 0xff;
		}
	}

	return { pixels: outData, airMask };
}

// ---------------------------------------------------------------------------
// Scene material identity, for the edge-decal pass
//
// PixelScene_TryPaintEntry @0x00880fb0 runs its OWN EdgeGraphics pass after
// writing a scene's cells (BiomeMaterials_PaintEdgeMaterial @0x00721da0), so
// scene-painted materials get dressed exactly like generated terrain. That pass
// needs the material IDENTITY of every scene pixel, which is the same
// classification texturePixelSceneForBiome() runs for colors:
//   alpha 0 / #000000  -> untouched, the world cell under it stays
//   #000042            -> FORCE AIR, the world cell is removed
//   gray/white         -> density 1.0 through the biome's band chooser
//   anything else      -> the wang color's own material
//
// The gates are on material IDENTITY alone. A scene's _visual.png art overrides
// only the cell COLOR, never its material, so art cells participate in the edge
// decisions exactly like the material under them -- and the engine happily
// stamps over art at a real material border. The one thing that stops a scene's
// pass is `skip_edge_textures` (js/pixel_scene_edge_flags.js), which suppresses
// the restamp entirely while the scene's cells still erase the terrain pass.
// ---------------------------------------------------------------------------

/** The scene leaves this pixel alone; whatever cell the world has stays. */
export const SCENE_MAT_UNTOUCHED = -2;
/** A painted cell whose material the engine tables don't carry (prop art,
 *  unmapped wang colors). It blocks edges like any solid cell but can never
 *  stamp decals of its own. Large so it can live in an Int16Array beside
 *  real ids without colliding with them. */
export const SCENE_MAT_UNKNOWN = 0x7ffe;

/**
 * The per-pixel material ids one stamped scene instance paints, in the engine's
 * classification above. `bands` is the engine_resolve/band_select module (passed
 * in so this file keeps not importing the engine tables statically).
 *
 * Returns { grid: Int16Array, width, height, x, y, skipEdges } or null when the
 * scene's pixels aren't loaded. `skipEdges` is the game's `skip_edge_textures`:
 * the cells still erase the terrain stamps under them, but no restamp runs.
 */
export function pixelSceneMaterialGrid(scene, bands) {
	const data = PIXEL_SCENE_DATA[scene.key];
	if (!data || !ArrayBuffer.isView(data.imgElement)) return null;
	// Material substitutions first, in wang color space, exactly as the bitmap
	// build runs them; the biome part names the band table for the gray class.
	let pixels = data.imgElement;
	let biome = 'general';
	for (const part of (scene.variantKey || '').split('&')) {
		const eq = part.indexOf('=');
		if (eq < 0) continue;
		if (part.slice(0, eq) === 'biome') biome = part.slice(eq + 1);
		else pixels = recolorPixelScene(pixels, parseInt(part.slice(0, eq), 16), parseInt(part.slice(eq + 1), 16));
	}
	const at = biome.indexOf('@');
	const underlyingBiome = at < 0 ? null : biome.slice(at + 1);
	const targetBiome = at < 0 ? biome : biome.slice(0, at);
	const densityBiome = densityBiomeFor(bands, targetBiome, underlyingBiome);
	const fillMaterial = densityFillMaterialFor(targetBiome, underlyingBiome);
	const fillId = fillMaterial ? bands.materialIdForName(fillMaterial.material) : -1;

	const w = data.width, h = data.height;
	const grid = new Int16Array(w * h).fill(SCENE_MAT_UNTOUCHED);
	const wangIds = new Map();
	for (let p = 0, i = 0; p < w * h; p++, i += 4) {
		if (pixels[i + 3] === 0) continue;
		const r = pixels[i], g = pixels[i + 1], b = pixels[i + 2];
		if (r === 0x00 && g === 0x00 && b === 0x42) { grid[p] = 0; continue; }
		if (r === g && g === b && r > 0) {
			const wx = scene.x + (p % w), wy = scene.y + ((p / w) | 0);
			const id = densityBiome
				? bands.selectComponentForCell(densityBiome.entry, wx, wy,
					bands.computeMaterialNoiseDensity(wx, wy, 1.0))
				: -1;
			// Same rule the bitmap build paints: a table we have answering -1
			// is AIR (id 0), and only a biome with no table at all falls back
			// to its flat fill material.
			grid[p] = id >= 0 ? id
				: densityBiome ? 0
					: (fillId >= 0 ? fillId : SCENE_MAT_UNKNOWN);
			continue;
		}
		const rgb = (r << 16) | (g << 8) | b;
		let id = wangIds.get(rgb);
		if (id === undefined) {
			const name = materialForWangColor(rgb);
			id = name ? bands.materialIdForName(name) : -1;
			wangIds.set(rgb, id);
		}
		grid[p] = id >= 0 ? id : SCENE_MAT_UNKNOWN;
	}
	return {
		grid, width: w, height: h, x: scene.x, y: scene.y,
		skipEdges: !!data.skipEdgeTextures,
		// The colors-file coverage mask. The scene's edge pass is gated on it as
		// a whole (js/edge_decals.js sceneStampsAnyEdge), so the decal pass needs
		// it alongside the materials.
		artMask: data.artMask || null,
	};
}

// ---------------------------------------------------------------------------
// The material map of one scene variant, for the GL scene pass (see "Scene
// material maps" above). Runs in the overlay worker.
//
// The classification is texturePixelSceneForBiome's, with everything that
// depends on WHERE the scene lands left for the shader:
// The cell kinds are js/scene_cells.js. The kind is the texel's alpha byte. A
// second layer, when the scene has colors-file art, is that art padded to the
// scene's size (straight alpha).
// ---------------------------------------------------------------------------

/**
 * @param {object} data         the scene's PIXEL_SCENE_DATA record, its pixels
 *        (and art) decoded. The record, not its key: a metadata re-sync swaps
 *        the table's records, and one that lands while this scene is being
 *        decoded leaves the pixels on the record the caller holds.
 * @param {string} variantKey   material substitutions and the `biome=` part
 * @param {object} bands        engine_resolve/band_select.js
 * @param {Array<[number, number]>} texturedAlphas  texturedFlatAlphas(): the
 *        textured materials that are not opaque, which this thread cannot know
 * @returns {{width, height, layers, data: Uint8Array, erase: boolean}|null}
 *          `erase`: some cell can remove the world cell under it without
 *          covering it (air, a translucent material), so the scene needs the
 *          erase pass
 */
export function buildSceneMaterialMap(data, variantKey, bands, texturedAlphas = []) {
	if (!data || !ArrayBuffer.isView(data.imgElement)) return null;
	let pixels = data.imgElement;
	let biome = 'general';
	for (const part of (variantKey || '').split('&')) {
		const eq = part.indexOf('=');
		if (eq < 0) continue;
		if (part.slice(0, eq) === 'biome') biome = part.slice(eq + 1);
		else pixels = recolorPixelScene(pixels, parseInt(part.slice(0, eq), 16), parseInt(part.slice(eq + 1), 16));
	}
	const paint = sceneBiomePaint(data.name, biome);
	const texAlpha = new Map(texturedAlphas);
	const translucent = (rgb) => texAlpha.has(rgb) || flatMaterialAlpha(rgb) < 255;
	const pack = (kind, r, g, b) => ((kind << 24) | (b << 16) | (g << 8) | r) >>> 0;   // little-endian RGBA
	const packColor = (kind, rgb) => pack(kind, (rgb >> 16) & 0xff, (rgb >> 8) & 0xff, rgb & 0xff);
	let erase = false;

	// FORCE AIR: removed from the world, unless this scene paints its air solid.
	const airCode = paint.texturedAirAlpha === 0x00 ? pack(SCENE_CELL_AIR, 0, 0, 0) : packColor(SCENE_CELL_COLOR, paint.bgColor);
	// The density class: the biome's band table where it has one, else its fill
	// material, else the flat fill color.
	const densityBiome = densityBiomeFor(bands, paint.targetBiome, paint.underlyingBiome);
	let densityCode, densityErases = false;
	if (densityBiome) {
		// The chooser can answer air, or a translucent band material.
		densityCode = pack(SCENE_CELL_DENSITY, bands.engineBiomeSlot(densityBiome.entry), 0, 0);
		densityErases = true;
	} else {
		const fill = densityFillMaterialFor(paint.targetBiome, paint.underlyingBiome);
		const id = fill ? bands.materialIdForName(fill.material) : -1;
		if (id > 0) {
			densityCode = pack(SCENE_CELL_MATERIAL, id & 0xff, id >> 8, 0);
			const wang = MATERIAL_WANG_COLORS[fill.material];
			densityErases = wang !== undefined && translucent(parseInt(wang, 16) & 0xffffff);
		} else {
			densityCode = packColor(SCENE_CELL_COLOR, paint.targetColor);
		}
	}

	const w = data.width, h = data.height, n = w * h;
	const art = data.visualArt;
	const layers = art ? 2 : 1;
	const out = new Uint8Array(n * 4 * layers);
	const cells = new Uint32Array(out.buffer, 0, n);
	// A scene is long runs of a few colors: the last one answers most pixels.
	let lastRgb = -1, lastCode = 0, lastErases = false;
	for (let p = 0, i = 0; p < n; p++, i += 4) {
		if (pixels[i + 3] === 0) continue;
		const r = pixels[i], g = pixels[i + 1], b = pixels[i + 2];
		const rgb = (r << 16) | (g << 8) | b;
		if (rgb !== lastRgb) {
			lastRgb = rgb;
			if (rgb === 0x000042) {
				lastCode = airCode;
				lastErases = paint.texturedAirAlpha === 0x00;
			} else if (r === g && g === b && r > 0) {
				lastCode = densityCode;
				lastErases = densityErases;
			} else {
				const name = materialForWangColor(rgb);
				const id = name ? bands.materialIdForName(name) : -1;
				if (id > 0) {
					lastCode = pack(SCENE_CELL_MATERIAL, id & 0xff, id >> 8, 0);
					lastErases = translucent(rgb);
				} else if (id === 0) {
					lastCode = pack(SCENE_CELL_AIR, 0, 0, 0);
					lastErases = true;
				} else {
					lastCode = packColor(SCENE_CELL_COLOR, MATERIAL_COLOR_CONVERSION[rgb] ?? rgb);
					lastErases = false;
				}
			}
		}
		cells[p] = lastCode;
		if (lastErases) erase = true;
	}
	if (art) {
		// Anchored top-left; the art may be smaller than the scene (hall_b).
		const aw = Math.min(w, art.width), ah = Math.min(h, art.height);
		for (let y = 0; y < ah; y++) {
			out.set(art.data.subarray(y * art.width * 4, (y * art.width + aw) * 4), (n + y * w) * 4);
		}
	}
	return { width: w, height: h, layers, data: out, erase };
}
